import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useDispatch, useSelector } from 'react-redux';
import { formatDateTimeKST, formatDecimal, formatDecimalFixed, formatWithCommas, removeCommas } from '../utils/dateUtils';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Typography,
  Collapse,
  Grid,
  InputAdornment,
  RadioGroup,
  Radio,
  keyframes,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Refresh,
  PlayArrow,
  Pause,
  Delete,
  Add,
  Save,
  Cancel as CancelIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Notifications,
  NotificationsOff,
  TrendingUp,
  TrendingDown,
  StopCircle,
} from '@mui/icons-material';
import { 
  fetchTriggers, 
  createTrigger,
  createTriggerGrid,
  updateTrigger,
  toggleTriggerStatus,
  deleteTrigger,
  setFilters,
  updateSingleTrigger,
  updateManyTriggers,
  removeSingleTrigger
} from '../store/triggerSlice';
import { fetchTradingPairs } from '../store/tradingPairSlice';
import { fetchWalletBalances } from '../store/walletBalanceSlice';
import { CryptoIcon, ExchangeIcon } from '../components/IconDisplay';
import TriggerGridCreateDialog from '../components/TriggerGridCreateDialog';
import SignedNumberField from '../components/SignedNumberField';
import wsService from '../services/websocket';
import { PAGE_REFRESH_MS, STATUS_POLL_MS, NOTIFICATION_SWEEP_MS, WS_FALLBACK_POLL_MS } from '../config/intervals';
import { useConfirm } from '../components/ConfirmDialog';

const PREMIUM_RENDER_BATCH_MS = 50;
const TRIGGER_RENDER_BATCH_MS = 100;

const toComparableNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const areMemoryStatesEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;

  return (
    toComparableNumber(a.capital?.target_entered) === toComparableNumber(b.capital?.target_entered) &&
    toComparableNumber(a.capital?.origin_entered) === toComparableNumber(b.capital?.origin_entered) &&
    toComparableNumber(a.positions?.target) === toComparableNumber(b.positions?.target) &&
    toComparableNumber(a.positions?.origin) === toComparableNumber(b.positions?.origin) &&
    toComparableNumber(a.pending_orders) === toComparableNumber(b.pending_orders)
  );
};

// Helper function to format compact numbers (e.g., 1.2M, 500K)
const formatCompactNumber = (num) => {
  if (num === null || num === undefined) return 'N/A';
  const value = Number(num);
  if (isNaN(value)) return 'N/A';

  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toFixed(0);
};

// Animation for flashing rows - green for LS (long-short) - ultra short
const flashAnimationLS = keyframes`
  0% { background-color: transparent; }
  40% { background-color: rgba(76, 175, 80, 0.6); }
  60% { background-color: rgba(76, 175, 80, 0.6); }
  100% { background-color: transparent; }
`;

// Animation for flashing rows - red for SL (short-long) - ultra short
const flashAnimationSL = keyframes`
  0% { background-color: transparent; }
  40% { background-color: rgba(244, 67, 54, 0.6); }
  60% { background-color: rgba(244, 67, 54, 0.6); }
  100% { background-color: transparent; }
`;

// MUI Chip color name for a trigger's status. Pure; module-scope so
// TriggerRow (React.memo) has a stable reference.
const getStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'stopped':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};

// Helper function to check if hedge is broken (positions not balanced within tolerance)
const isHedgeBroken = (targetPos, originPos, toleranceRate = 0.01) => {
  // Both positions must exist
  if (targetPos === null || targetPos === undefined ||
      originPos === null || originPos === undefined) {
    return false;
  }

  const maxPosition = Math.max(Math.abs(targetPos), Math.abs(originPos));

  // If no positions, not broken
  if (maxPosition === 0) {
    return false;
  }

  // For proper hedge: target + origin should be ~0
  const totalPosition = targetPos + originPos;
  const discrepancyRate = Math.abs(totalPosition) / maxPosition;

  return discrepancyRate > toleranceRate;
};

// Toast notification component. Uses useMediaQuery internally so it can
// render in a much more compact form on phones without needing a prop
// threaded through from the parent.
function TradeNotification({ notification, onClose }) {
  const isLS = notification.direction === 'LS';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Alert
      severity={isLS ? "success" : "error"}
      onClose={onClose}
      icon={isLS ? <TrendingDown fontSize={isMobile ? 'small' : 'medium'} /> : <TrendingUp fontSize={isMobile ? 'small' : 'medium'} />}
      sx={{
        mb: 1,
        boxShadow: 2,
        p: isMobile ? '2px 8px' : undefined,
        fontSize: isMobile ? '0.7rem' : undefined,
        '& .MuiAlert-icon': {
          color: isLS ? 'success.main' : 'error.main',
          mr: isMobile ? 0.75 : undefined,
          py: isMobile ? 0.5 : undefined,
        },
        '& .MuiAlert-message': {
          py: isMobile ? 0.25 : undefined,
        },
        '& .MuiAlert-action': {
          pt: isMobile ? 0 : undefined,
          pl: isMobile ? 0.5 : undefined,
        },
      }}
    >
      <Box>
        <Typography
          variant={isMobile ? 'caption' : 'subtitle2'}
          fontWeight="bold"
          sx={{ display: 'block', fontSize: isMobile ? '0.75rem' : undefined }}
        >
          {notification.symbol} {isMobile ? (isLS ? 'LS' : 'SL') : (isLS ? 'Long-Short (LS)' : 'Short-Long (SL)')}
          {!isMobile && ' Executed'}
        </Typography>

        {/* Exchange flow visualization */}
        {notification.target_exchange && notification.origin_exchange && (
          <Box display="flex" alignItems="center" gap={0.25} my={isMobile ? 0 : 0.5}>
            <ExchangeIcon exchangeName={notification.target_exchange} size={isMobile ? 12 : 16} />
            <Typography variant="caption" color="text.secondary" sx={{ mx: 0.25, fontSize: isMobile ? '0.6rem' : undefined }}>
              ↔
            </Typography>
            <ExchangeIcon exchangeName={notification.origin_exchange} size={isMobile ? 12 : 16} />
          </Box>
        )}

        <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : undefined, lineHeight: isMobile ? 1.3 : undefined }}>
          Premium: {notification.premium_display}
        </Typography>
        {notification.quantity && (
          <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem', lineHeight: isMobile ? 1.3 : undefined }}>
            {isMobile ? 'Qty' : 'Quantity'}: {notification.quantity.toFixed(isMobile ? 4 : 8)} {notification.symbol}
          </Typography>
        )}
        {notification.order_rate && !isMobile && (
          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            Order Rate: {notification.order_rate} orders/sec
          </Typography>
        )}
      </Box>
    </Alert>
  );
}

// Memoized row component for the Triggers table. Extracted from the inline
// sortedTriggers.map() so that high-frequency updates (premium_update,
// memory_state_update, ...) only re-render the rows whose data actually
// changed. All handlers passed in are useCallback-stabilized in the parent
// so React.memo's default shallow prop comparison can skip rows whose pair-
// keyed or trigger-keyed state did not update.
const TriggerRow = React.memo(function TriggerRow({
  trigger,
  hasData,
  connStatus,
  isPairActive,
  isMobile,
  isExpanded,
  isFlashing,
  flashDirection,
  premium,
  memState,
  editing,
  onToggleExpand,
  onToggleStatus,
  onDelete,
  onOpenCapitalDetail,
  onOpenManualOrder,
  onCreatedClick,
  onEditingChange,
  onCancelEdit,
  onSaveInlineEdit,
  onToggleSkipLow,
  onToggleSkipHigh,
}) {
  return (
    <>
                  <TableRow
                    sx={{
                      animation: isFlashing 
                        ? `${flashDirection === 'LS' ? flashAnimationLS : flashAnimationSL} 0.1s ease-in-out` 
                        : 'none'
                    }}
                  >
                    <TableCell sx={{ px: isMobile ? 0.25 : 2, py: isMobile ? 0.25 : 1 }}>
                      <Box display="flex" alignItems="center" gap={0}>
                        <IconButton
                          size="small"
                          onClick={() => onToggleExpand(trigger)}
                          sx={{ p: isMobile ? 0.25 : 0.5 }}
                          aria-label={isExpanded ? `Collapse ${trigger.trading_pair_symbol} trigger details` : `Expand ${trigger.trading_pair_symbol} trigger details`}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? <KeyboardArrowUp sx={{ fontSize: isMobile ? 14 : 24 }} /> : <KeyboardArrowDown sx={{ fontSize: isMobile ? 14 : 24 }} />}
                        </IconButton>
                        <Tooltip title={
                          trigger.status === 'active'
                            ? 'Pause'
                            : (!hasData ? 'Cannot activate - orderbooks not available' : 'Activate')
                        }>
                          <span>
                            <IconButton
                              onClick={() => onToggleStatus(trigger.uuid)}
                              size="small"
                              disabled={trigger.status !== 'active' && !hasData}
                              sx={{
                                p: isMobile ? 0.25 : 0.5,
                                color: trigger.status === 'active' ? 'success.main' : 'error.main'
                              }}
                              aria-label={trigger.status === 'active' ? `Pause ${trigger.trading_pair_symbol} trigger` : `Activate ${trigger.trading_pair_symbol} trigger`}
                            >
                              {trigger.status === 'active' ? <Pause sx={{ fontSize: isMobile ? 14 : 24 }} /> : <PlayArrow sx={{ fontSize: isMobile ? 14 : 24 }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => onDelete(trigger.uuid)}
                            size="small"
                            color="error"
                            sx={{ p: isMobile ? 0.25 : 0.5 }}
                            aria-label={`Delete ${trigger.trading_pair_symbol} trigger`}
                          >
                            <Delete sx={{ fontSize: isMobile ? 14 : 24 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      pl: isMobile ? 0.25 : 1,
                      pr: isMobile ? 0.25 : 0.5,
                      py: isMobile ? 0.25 : 1,
                      bgcolor: !isPairActive
                        ? 'rgba(244, 67, 54, 0.08)' // Shallow red when trading pair is deactivated
                        : (hasData ? 'rgba(76, 175, 80, 0.08)' : 'inherit') // Shallow green when both orderbooks have data
                    }}>
                      <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1}>
                      <CryptoIcon symbol={trigger.trading_pair_target_symbol || trigger.trading_pair_symbol} size={isMobile ? 14 : 24} />
                      <Box>
                        <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1} flexWrap={isMobile ? 'wrap' : 'nowrap'}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.7rem' : undefined }}>
                            {trigger.trading_pair_symbol}
                          </Typography>
                          {premium && (() => {
                            const lsP = premium?.ls_premium;
                            const slP = premium?.sl_premium;
                            const lsArmed = lsP !== null && lsP !== undefined && lsP <= trigger.low && !trigger.skip_low;
                            const slArmed = slP !== null && slP !== undefined && slP >= trigger.high && !trigger.skip_high;
                            return (
                              <Box display="flex" gap={isMobile ? 0.25 : 1}>
                                <Tooltip
                                  title={`LS (Long-Short) — fires when premium ≤ Low (${formatDecimal(trigger.low)}%)${trigger.skip_low ? ' · currently skipped' : ''}`}
                                  arrow
                                >
                                  <Chip
                                    size="small"
                                    label={`LS: ${lsP === null || lsP === undefined ? 'N/A  ' : `${formatDecimalFixed(lsP)}%`}`}
                                    sx={{
                                      bgcolor: lsArmed ? 'success.main' : 'transparent',
                                      color: lsArmed ? 'success.contrastText' : 'text.secondary',
                                      border: '1px solid',
                                      borderColor: lsArmed ? 'success.main' : 'divider',
                                      fontWeight: lsArmed ? 'bold' : 'normal',
                                      fontSize: isMobile ? '0.55rem' : '0.7rem',
                                      height: isMobile ? '14px' : '20px',
                                      '& .MuiChip-label': { px: isMobile ? 0.5 : 1 },
                                      fontFamily: 'monospace',
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip
                                  title={`SL (Short-Long) — fires when premium ≥ High (${formatDecimal(trigger.high)}%)${trigger.skip_high ? ' · currently skipped' : ''}`}
                                  arrow
                                >
                                  <Chip
                                    size="small"
                                    label={`SL: ${slP === null || slP === undefined ? 'N/A  ' : `${formatDecimalFixed(slP)}%`}`}
                                    sx={{
                                      bgcolor: slArmed ? 'error.main' : 'transparent',
                                      color: slArmed ? 'error.contrastText' : 'text.secondary',
                                      border: '1px solid',
                                      borderColor: slArmed ? 'error.main' : 'divider',
                                      fontWeight: slArmed ? 'bold' : 'normal',
                                      fontSize: isMobile ? '0.55rem' : '0.7rem',
                                      height: isMobile ? '14px' : '20px',
                                      '& .MuiChip-label': { px: isMobile ? 0.5 : 1 },
                                      fontFamily: 'monospace',
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                            );
                          })()}
                        </Box>
                        <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1} sx={{ mt: 0.5 }}>
                          <ExchangeIcon exchangeName={trigger.target_exchange_name} size={isMobile ? 9 : 14} />
                          <Typography
                            variant="caption"
                            sx={{ color: connStatus?.target_connected ? 'success.main' : 'text.secondary', fontSize: isMobile ? '0.7rem' : undefined }}
                          >
                            {isMobile ? `T: ${trigger.target_exchange_name}` : `Target: ${trigger.target_exchange_name} - ${trigger.target_market_name} (${trigger.target_quote_asset})`}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1}>
                          <ExchangeIcon exchangeName={trigger.origin_exchange_name} size={isMobile ? 9 : 14} />
                          <Typography
                            variant="caption"
                            sx={{ color: connStatus?.origin_connected ? 'success.main' : 'text.secondary', fontSize: isMobile ? '0.7rem' : undefined }}
                          >
                            {isMobile ? `O: ${trigger.origin_exchange_name}` : `Origin: ${trigger.origin_exchange_name} - ${trigger.origin_market_name} (${trigger.origin_quote_asset})`}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{
                    px: isMobile ? 0.25 : 2,
                    py: isMobile ? 0.25 : 1,
                    position: 'relative',
                    background: (() => {
                      // LS position fill (target_position > 0 means we have LS position)
                      // Use memory state (prop) for real-time position data
                      const targetPos = memState?.positions?.target || 0;
                      const enteredCapital = memState?.capital?.target_entered || 0;
                      const tradeCapital = parseFloat(trigger.trade_capital) || 1;
                      if (targetPos > 0.0001 && enteredCapital > 0) {
                        const fillPercent = Math.min(100, (enteredCapital / tradeCapital) * 100);
                        return `linear-gradient(to top, rgba(76, 175, 80, 0.25) ${fillPercent}%, transparent ${fillPercent}%)`;
                      }
                      return 'transparent';
                    })()
                  }}>
                    <Box>
                      {/* Threshold value */}
                      <Box sx={{ fontSize: isMobile ? '0.7rem' : undefined }}>
                        {formatDecimal(trigger.low)}%
                      </Box>
                      {/* Avg LS Premium */}
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.55rem' : '0.7rem', display: 'block' }}>
                        {trigger.avg_LS_premium !== null && trigger.avg_LS_premium !== undefined
                          ? `${formatDecimal(trigger.avg_LS_premium)}%`
                          : '-'}
                      </Typography>
                      {/* Avg LS Slippage */}
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: isMobile ? '0.55rem' : '0.7rem',
                          display: 'block',
                          color: trigger.avg_LS_slippage_p > 0 ? 'error.main' : trigger.avg_LS_slippage_p < 0 ? 'success.main' : 'text.secondary'
                        }}
                      >
                        {trigger.avg_LS_slippage_p !== null && trigger.avg_LS_slippage_p !== undefined
                          ? `${formatDecimal(trigger.avg_LS_slippage_p)}%`
                          : '-'}
                      </Typography>
                      {/* Skip Low Toggle */}
                      <Chip
                        label={trigger.skip_low ? "Skip" : "Active"}
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onToggleSkipLow(trigger); }}
                        sx={{
                          mt: 0.5,
                          cursor: 'pointer',
                          height: isMobile ? '12px' : '18px',
                          fontSize: isMobile ? '0.55rem' : '0.7rem',
                          bgcolor: trigger.skip_low ? 'warning.light' : 'success.light',
                          color: trigger.skip_low ? 'warning.contrastText' : 'success.contrastText',
                          '& .MuiChip-label': { px: isMobile ? 0.5 : 1 },
                          '&:hover': {
                            bgcolor: trigger.skip_low ? 'warning.main' : 'success.main',
                          }
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{
                    px: isMobile ? 0.25 : 2,
                    py: isMobile ? 0.25 : 1,
                    position: 'relative',
                    background: (() => {
                      // SL position fill (target_position < 0 means we have SL position)
                      // Only show for futures-vs-futures (SPOT origin can't have SL)
                      // Use memory state (prop) for real-time position data
                      const targetPos = memState?.positions?.target || 0;
                      const enteredCapital = memState?.capital?.target_entered || 0;
                      const tradeCapital = parseFloat(trigger.trade_capital) || 1;
                      const isSpotOrigin = trigger.origin_market_name === 'SPOT';
                      if (!isSpotOrigin && targetPos < -0.0001 && enteredCapital > 0) {
                        const fillPercent = Math.min(100, (enteredCapital / tradeCapital) * 100);
                        return `linear-gradient(to top, rgba(244, 67, 54, 0.25) ${fillPercent}%, transparent ${fillPercent}%)`;
                      }
                      return 'transparent';
                    })()
                  }}>
                    <Box>
                      {/* Threshold value */}
                      <Box sx={{ fontSize: isMobile ? '0.7rem' : undefined }}>
                        {formatDecimal(trigger.high)}%
                      </Box>
                      {/* Avg SL Premium */}
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.55rem' : '0.7rem', display: 'block' }}>
                        {trigger.avg_SL_premium !== null && trigger.avg_SL_premium !== undefined
                          ? `${formatDecimal(trigger.avg_SL_premium)}%`
                          : '-'}
                      </Typography>
                      {/* Avg SL Slippage */}
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: isMobile ? '0.55rem' : '0.7rem',
                          display: 'block',
                          color: trigger.avg_SL_slippage_p > 0 ? 'error.main' : trigger.avg_SL_slippage_p < 0 ? 'success.main' : 'text.secondary'
                        }}
                      >
                        {trigger.avg_SL_slippage_p !== null && trigger.avg_SL_slippage_p !== undefined
                          ? `${formatDecimal(trigger.avg_SL_slippage_p)}%`
                          : '-'}
                      </Typography>
                      {/* Skip High Toggle */}
                      <Chip
                        label={trigger.skip_high ? "Skip" : "Active"}
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onToggleSkipHigh(trigger); }}
                        sx={{
                          mt: 0.5,
                          cursor: 'pointer',
                          height: isMobile ? '12px' : '18px',
                          fontSize: isMobile ? '0.55rem' : '0.7rem',
                          bgcolor: trigger.skip_high ? 'warning.light' : 'success.light',
                          color: trigger.skip_high ? 'warning.contrastText' : 'success.contrastText',
                          '& .MuiChip-label': { px: isMobile ? 0.5 : 1 },
                          '&:hover': {
                            bgcolor: trigger.skip_high ? 'warning.main' : 'success.main',
                          }
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => onOpenCapitalDetail(trigger.trading_pair)}
                    sx={{
                      px: isMobile ? 0.25 : 2,
                      py: isMobile ? 0.25 : 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <Box>
                      <Box sx={{ fontSize: isMobile ? '0.7rem' : undefined }}>
                        {trigger.trade_qty ?
                          `${formatWithCommas(trigger.trade_qty)} ${trigger.trading_pair_symbol}` :
                          `${formatWithCommas(trigger.trade_capital) || '-'} ${(trigger.target_quote_asset === 'USDT' && trigger.origin_quote_asset === 'USDT') ? 'USDT' : '원'}`
                        }
                      </Box>
                      {/* In-memory capital (2nd row) */}
                      <Typography variant="caption" sx={{ fontSize: isMobile ? '0.55rem' : '0.7rem', color: 'info.main', display: 'block' }}>
                        {memState ?
                          `M: ${formatWithCommas(Math.round(memState.capital?.target_entered || 0))} / ${formatWithCommas(Math.round(memState.capital?.origin_entered || 0))}`
                          : 'M: -'}
                      </Typography>
                      {/* Database capital (3rd row) */}
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.55rem' : '0.7rem', display: 'block' }}>
                        {trigger.entered_trade_capital
                          ? (typeof trigger.entered_trade_capital === 'object' && trigger.entered_trade_capital.display
                              ? `DB: ${trigger.entered_trade_capital.display}`
                              : typeof trigger.entered_trade_capital === 'string' && trigger.entered_trade_capital.includes('/')
                                ? `DB: ${trigger.entered_trade_capital.split('/').map(v => `${formatWithCommas(Math.round(Number(v)))}`).join('/')}${(trigger.target_quote_asset === 'USDT' && trigger.origin_quote_asset === 'USDT') ? '' : '원'}`
                                : trigger.entered_trade_capital > 0
                                  ? `DB: ${formatWithCommas(Math.round(trigger.entered_trade_capital))}${(trigger.target_quote_asset === 'USDT' && trigger.origin_quote_asset === 'USDT') ? '' : '원'}`
                                  : 'DB: -')
                          : 'DB: -'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => onOpenManualOrder(trigger)}
                    sx={{
                      px: isMobile ? 0.5 : 2,
                      py: isMobile ? 0.25 : 1,
                      cursor: 'pointer',
                      // Keep the M:/DB:/EX:/ROI rows on one line each on
                      // mobile. Without this the column was collapsing to
                      // ~50px and every line wrapped to two, doubling the
                      // row height.
                      minWidth: isMobile ? 130 : 'auto',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <Box>
                      {/* In-memory positions (2nd row) */}
                      <Box sx={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          component="span"
                          sx={{
                            color: isHedgeBroken(memState?.positions?.target, memState?.positions?.origin)
                              ? 'error.main'
                              : (memState?.positions?.target > 0 ? 'success.main' :
                                 memState?.positions?.target < 0 ? 'error.main' : 'info.main'),
                            fontSize: isMobile ? '0.55rem' : '0.7rem',
                            fontWeight: isHedgeBroken(memState?.positions?.target, memState?.positions?.origin) ? 'bold' : 'normal'
                          }}
                        >
                          {isHedgeBroken(memState?.positions?.target, memState?.positions?.origin)
                            ? <Box component="span" sx={{ color: 'error.main' }}>M:</Box>
                            : 'M:'} {memState?.positions?.target !== undefined ?
                            `${memState.positions.target.toFixed(isMobile ? 2 : 4)}` : '-'}
                        </Typography>
                        <Typography component="span" sx={{ fontSize: isMobile ? '0.55rem' : '0.7rem' }}> / </Typography>
                        <Typography
                          component="span"
                          sx={{
                            color: memState?.positions?.origin > 0 ? 'success.main' :
                                   memState?.positions?.origin < 0 ? 'error.main' : 'info.main',
                            fontSize: isMobile ? '0.55rem' : '0.7rem'
                          }}
                        >
                          {memState?.positions?.origin !== undefined ?
                            `${memState.positions.origin.toFixed(isMobile ? 2 : 4)}` : '-'}
                        </Typography>
                      </Box>
                      {/* Database positions (3rd row) */}
                      <Box sx={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          component="span"
                          sx={{
                            color: isHedgeBroken(trigger.target_position, trigger.origin_position)
                              ? 'error.main'
                              : (trigger.target_position > 0 ? 'success.main' :
                                 trigger.target_position < 0 ? 'error.main' : 'text.secondary'),
                            fontWeight: isHedgeBroken(trigger.target_position, trigger.origin_position) ? 'bold' : 'medium',
                            fontSize: isMobile ? '0.55rem' : '0.7rem'
                          }}
                        >
                          {isHedgeBroken(trigger.target_position, trigger.origin_position)
                            ? <Box component="span" sx={{ color: 'error.main' }}>DB:</Box>
                            : 'DB:'} {trigger.target_position ? trigger.target_position.toFixed(isMobile ? 2 : 4) : '-'}
                        </Typography>
                        <Typography component="span" sx={{ fontSize: isMobile ? '0.55rem' : '0.7rem' }}> / </Typography>
                        <Typography
                          component="span"
                          sx={{
                            color: trigger.origin_position > 0 ? 'success.main' :
                                   trigger.origin_position < 0 ? 'error.main' : 'text.secondary',
                            fontWeight: 'medium',
                            fontSize: isMobile ? '0.55rem' : '0.7rem'
                          }}
                        >
                          {trigger.origin_position ? trigger.origin_position.toFixed(isMobile ? 2 : 4) : '-'}
                        </Typography>
                      </Box>
                      {/* Exchange positions (4th row) */}
                      <Box sx={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          component="span"
                          sx={{
                            color: isHedgeBroken(trigger.exchange_target_position, trigger.exchange_origin_position)
                              ? 'error.main'
                              : (trigger.exchange_target_position > 0 ? 'success.main' :
                                 trigger.exchange_target_position < 0 ? 'error.main' : 'text.secondary'),
                            fontWeight: isHedgeBroken(trigger.exchange_target_position, trigger.exchange_origin_position) ? 'bold' : 'medium',
                            fontSize: isMobile ? '0.55rem' : '0.7rem'
                          }}
                        >
                          {isHedgeBroken(trigger.exchange_target_position, trigger.exchange_origin_position)
                            ? <Box component="span" sx={{ color: 'error.main' }}>EX:</Box>
                            : 'EX:'} {trigger.exchange_target_position !== null && trigger.exchange_target_position !== undefined
                            ? trigger.exchange_target_position.toFixed(isMobile ? 2 : 4) : '-'}
                        </Typography>
                        <Typography component="span" sx={{ fontSize: isMobile ? '0.55rem' : '0.7rem' }}> / </Typography>
                        <Typography
                          component="span"
                          sx={{
                            color: trigger.exchange_origin_position > 0 ? 'success.main' :
                                   trigger.exchange_origin_position < 0 ? 'error.main' : 'text.secondary',
                            fontWeight: 'medium',
                            fontSize: isMobile ? '0.55rem' : '0.7rem'
                          }}
                        >
                          {trigger.exchange_origin_position !== null && trigger.exchange_origin_position !== undefined
                            ? trigger.exchange_origin_position.toFixed(isMobile ? 2 : 4) : '-'}
                        </Typography>
                      </Box>
                      {/* ROI% with leverage (5th row) - only for futures markets */}
                      <Box sx={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          component="span"
                          sx={{
                            color: trigger.target_roi_info?.roi_percent > 0 ? 'success.main' :
                                   trigger.target_roi_info?.roi_percent < 0 ? 'error.main' : 'text.secondary',
                            fontWeight: 'medium',
                            fontSize: isMobile ? '0.55rem' : '0.7rem'
                          }}
                        >
                          R: {trigger.target_roi_info
                            ? `${trigger.target_roi_info.roi_percent > 0 ? '+' : ''}${trigger.target_roi_info.roi_percent.toFixed(1)}%(${trigger.target_roi_info.leverage}x)`
                            : (trigger.target_market_name === 'SPOT' ? '-' : '-')}
                        </Typography>
                        <Typography component="span" sx={{ fontSize: isMobile ? '0.55rem' : '0.7rem' }}> / </Typography>
                        <Typography
                          component="span"
                          sx={{
                            color: trigger.origin_roi_info?.roi_percent > 0 ? 'success.main' :
                                   trigger.origin_roi_info?.roi_percent < 0 ? 'error.main' : 'text.secondary',
                            fontWeight: 'medium',
                            fontSize: isMobile ? '0.55rem' : '0.7rem'
                          }}
                        >
                          {trigger.origin_roi_info
                            ? `${trigger.origin_roi_info.roi_percent > 0 ? '+' : ''}${trigger.origin_roi_info.roi_percent.toFixed(1)}%(${trigger.origin_roi_info.leverage}x)`
                            : (trigger.origin_market_name === 'SPOT' ? '-' : '-')}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{
                    px: isMobile ? 0.5 : 2,
                    py: isMobile ? 0.25 : 1,
                    // PnL values like "R: +5432.12" used to wrap onto two
                    // lines on narrow mobile screens. Force a one-line
                    // layout per PnL row.
                    minWidth: isMobile ? 90 : 'auto',
                    whiteSpace: 'nowrap',
                  }}>
                    <Box>
                      {/* Realized PnL */}
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: isMobile ? '0.55rem' : '0.7rem',
                          display: 'block',
                          color: trigger.realized_pnl > 0 ? 'success.main' : trigger.realized_pnl < 0 ? 'error.main' : 'text.secondary',
                          fontWeight: trigger.realized_pnl !== null && trigger.realized_pnl !== undefined ? 'medium' : 'normal'
                        }}
                      >
                        {trigger.realized_pnl !== null && trigger.realized_pnl !== undefined
                          ? `R: ${trigger.realized_pnl > 0 ? '+' : ''}${trigger.realized_pnl.toFixed(2)}`
                          : 'R: -'}
                      </Typography>
                      {/* Unrealized PnL */}
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: isMobile ? '0.55rem' : '0.7rem',
                          display: 'block',
                          color: trigger.unrealized_pnl > 0 ? 'success.main' : trigger.unrealized_pnl < 0 ? 'error.main' : 'text.secondary',
                          fontWeight: trigger.unrealized_pnl !== null && trigger.unrealized_pnl !== undefined ? 'medium' : 'normal'
                        }}
                      >
                        {trigger.unrealized_pnl !== null && trigger.unrealized_pnl !== undefined
                          ? `U: ${trigger.unrealized_pnl > 0 ? '+' : ''}${trigger.unrealized_pnl.toFixed(2)}`
                          : 'U: -'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ px: isMobile ? 0.25 : 2, py: isMobile ? 0.25 : 1 }}>
                    <Tooltip
                      title={
                        trigger.status === 'active' ? 'Active — arbitrage detection running' :
                        trigger.status === 'stopped' ? 'Stopped — no orders will be placed' :
                        trigger.status === 'error' ? 'Error — manual intervention required' :
                        trigger.status
                      }
                      arrow
                    >
                      <Chip
                        label={trigger.status.toUpperCase()}
                        color={getStatusColor(trigger.status)}
                        size="small"
                        sx={{
                          height: isMobile ? '14px' : '20px',
                          fontSize: '0.7rem',
                          '& .MuiChip-label': { px: isMobile ? 0.5 : 0.75 }
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    onClick={() => onCreatedClick(trigger)}
                    sx={{
                      px: isMobile ? 0.25 : 2,
                      py: isMobile ? 0.25 : 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      {formatDateTimeKST(trigger.created_at)}
                    </Typography>
                  </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{
                          margin: isMobile ? 0.5 : 2,
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          p: isMobile ? 1 : 2
                        }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                  Low Value
                                </Typography>
                                <Box display="flex" gap={2} alignItems="center">
                                  <SignedNumberField
                                    fullWidth
                                    label="Low"
                                    value={editing?.low || ''}
                                    onChange={onEditingChange(trigger.uuid, 'low')}
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={editing?.skip_low || false}
                                        onChange={onEditingChange(trigger.uuid, 'skip_low')}
                                        size="small"
                                      />
                                    }
                                    label="Skip Low"
                                    sx={{ whiteSpace: 'nowrap' }}
                                  />
                                </Box>
                              </Paper>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                  High Value
                                </Typography>
                                <Box display="flex" gap={2} alignItems="center">
                                  <SignedNumberField
                                    fullWidth
                                    label="High"
                                    value={editing?.high || ''}
                                    onChange={onEditingChange(trigger.uuid, 'high')}
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={editing?.skip_high || false}
                                        onChange={onEditingChange(trigger.uuid, 'skip_high')}
                                        size="small"
                                      />
                                    }
                                    label="Skip High"
                                    sx={{ whiteSpace: 'nowrap' }}
                                  />
                                </Box>
                              </Paper>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                  {editing?.trade_mode === 'quantity' ? 'Quantity Settings' : 'Capital Settings'}
                                  <Typography variant="caption" sx={{ ml: 2, color: 'text.disabled' }}>
                                    (Mode: {editing?.trade_mode === 'capital_krw' ? 'KRW Capital' :
                                            editing?.trade_mode === 'capital_usdt' ? 'USDT Capital' : 'Quantity-based'})
                                  </Typography>
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      fullWidth
                                      label={editing?.trade_mode === 'quantity' ? 'Trade Quantity' :
                                             editing?.trade_mode === 'capital_usdt' ? 'Trade Capital (USDT)' : 'Trade Capital (원)'}
                                      value={editing?.trade_mode === 'quantity' ?
                                        (editing?.trade_qty || '') :
                                        formatWithCommas(editing?.trade_capital || '')}
                                      onChange={(e) => {
                                        const rawValue = removeCommas(e.target.value);
                                        if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                                          onEditingChange(trigger.uuid,
                                            editing?.trade_mode === 'quantity' ? 'trade_qty' : 'trade_capital')({ target: { value: rawValue } });
                                        }
                                      }}
                                      size="small"
                                      variant="outlined"
                                      InputProps={{
                                        endAdornment: <InputAdornment position="end">
                                          {editing?.trade_mode === 'quantity' ? trigger.trading_pair_symbol :
                                           editing?.trade_mode === 'capital_usdt' ? 'USDT' : '원'}
                                        </InputAdornment>,
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      fullWidth
                                      label={editing?.trade_mode === 'quantity' ? 'Min Order Quantity' :
                                             editing?.trade_mode === 'capital_usdt' ? 'Min Order Capital (USDT)' : 'Min Order Capital (원)'}
                                      value={editing?.trade_mode === 'quantity' ?
                                        (editing?.min_order_qty || '') :
                                        formatWithCommas(editing?.min_order_capital || '')}
                                      onChange={(e) => {
                                        const rawValue = removeCommas(e.target.value);
                                        if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                                          onEditingChange(trigger.uuid,
                                            editing?.trade_mode === 'quantity' ? 'min_order_qty' : 'min_order_capital')({ target: { value: rawValue } });
                                        }
                                      }}
                                      size="small"
                                      variant="outlined"
                                      InputProps={{
                                        endAdornment: <InputAdornment position="end">
                                          {editing?.trade_mode === 'quantity' ? trigger.trading_pair_symbol :
                                           editing?.trade_mode === 'capital_usdt' ? 'USDT' : '원'}
                                        </InputAdornment>,
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      fullWidth
                                      label={editing?.trade_mode === 'quantity' ? 'Max Order Quantity' :
                                             editing?.trade_mode === 'capital_usdt' ? 'Max Order Capital (USDT)' : 'Max Order Capital (원)'}
                                      value={editing?.trade_mode === 'quantity' ?
                                        (editing?.max_order_qty || '') :
                                        formatWithCommas(editing?.max_order_capital || '')}
                                      onChange={(e) => {
                                        const rawValue = removeCommas(e.target.value);
                                        if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                                          onEditingChange(trigger.uuid,
                                            editing?.trade_mode === 'quantity' ? 'max_order_qty' : 'max_order_capital')({ target: { value: rawValue } });
                                        }
                                      }}
                                      size="small"
                                      variant="outlined"
                                      InputProps={{
                                        endAdornment: <InputAdornment position="end">
                                          {editing?.trade_mode === 'quantity' ? trigger.trading_pair_symbol :
                                           editing?.trade_mode === 'capital_usdt' ? 'USDT' : '원'}
                                        </InputAdornment>,
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Allowed Slippage"
                                value={editing?.allowed_slippage || ''}
                                onChange={onEditingChange(trigger.uuid, 'allowed_slippage')}
                                type="number"
                                size="small"
                                step="0.1"
                                helperText="e.g., 0.1 for 0.1%"
                                variant="outlined"
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Remark"
                                value={editing?.remark || ''}
                                onChange={onEditingChange(trigger.uuid, 'remark')}
                                size="small"
                                variant="outlined"
                              />
                            </Grid>

                            {/* Position Mode Settings - Only for Futures vs Futures */}
                            {trigger.target_market_name !== 'SPOT' && trigger.origin_market_name !== 'SPOT' && (
                              <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                    Position Mode
                                  </Typography>
                                  <Box display="flex" gap={2} alignItems="center">
                                    <TextField
                                      select
                                      label="Position Mode"
                                      value={editing?.position_mode || 'bidirectional'}
                                      onChange={onEditingChange(trigger.uuid, 'position_mode')}
                                      size="small"
                                      sx={{ minWidth: 150 }}
                                    >
                                      <MenuItem value="bidirectional">Bidirectional</MenuItem>
                                      <MenuItem value="unidirectional">Unidirectional</MenuItem>
                                    </TextField>
                                    {editing?.position_mode === 'unidirectional' && (
                                      <TextField
                                        select
                                        label="Allowed Direction"
                                        value={editing?.allowed_direction || 'ls_to_sl'}
                                        onChange={onEditingChange(trigger.uuid, 'allowed_direction')}
                                        size="small"
                                        sx={{ minWidth: 150 }}
                                      >
                                        <MenuItem value="ls_to_sl">LS → SL</MenuItem>
                                        <MenuItem value="sl_to_ls">SL → LS</MenuItem>
                                      </TextField>
                                    )}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    {editing?.position_mode === 'bidirectional'
                                      ? 'Normal mode: Can trade in both directions freely.'
                                      : editing?.allowed_direction === 'ls_to_sl'
                                        ? 'Only open LS positions. SL only closes existing LS positions.'
                                        : 'Only open SL positions. LS only closes existing SL positions.'}
                                  </Typography>
                                </Paper>
                              </Grid>
                            )}

                            <Grid item xs={12}>
                              <Box display="flex" gap={2} justifyContent="flex-start" sx={{ mt: 1 }}>
                                <Button
                                  variant="text"
                                  size="medium"
                                  startIcon={<CancelIcon />}
                                  onClick={() => onCancelEdit(trigger.uuid)}
                                  sx={{ px: 3 }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="contained"
                                  size="medium"
                                  startIcon={<Save />}
                                  onClick={() => onSaveInlineEdit(trigger.uuid, editing)}
                                  sx={{ px: 3 }}
                                >
                                  Save Changes
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
    </>
  );
});

function Triggers() {
  const dispatch = useDispatch();
  // Shared confirmation dialog; replaces blocking window.confirm().
  const { confirm, dialog: confirmDialog } = useConfirm();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { triggers, loading, filters } = useSelector((state) => state.triggers);
  const { tradingPairs } = useSelector((state) => state.tradingPairs);
  const { balances: walletBalances } = useSelector((state) => state.walletBalances);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [gridDialogOpen, setGridDialogOpen] = useState(false);
  const [manualOrderDialogOpen, setManualOrderDialogOpen] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [manualOrderData, setManualOrderData] = useState({
    target_quantity: 0,
    target_side: 'buy',
    origin_quantity: 0,
    origin_side: 'buy',
    track_position: false
  });
  const [manualOrderLoading, setManualOrderLoading] = useState(false);
  const [capitalDetailOpen, setCapitalDetailOpen] = useState(false);
  const [selectedCapitalTradingPair, setSelectedCapitalTradingPair] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [orderbookData, setOrderbookData] = useState({});
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [flashingTriggers, setFlashingTriggers] = useState(new Map()); // Map to store trigger_id -> direction
  const [memoryStates, setMemoryStates] = useState({});  // Store in-memory states by trigger UUID
  const [wsConnected, setWsConnected] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [premiumData, setPremiumData] = useState({}); // Store real-time premiums {trading_pair_id: {ls_premium, sl_premium}}
  // NOTE: pnlData state was removed — it was written on every pnl_update
  // WebSocket message (~10/s) but never read anywhere in render, which meant
  // each message forced a Triggers re-render for no visible effect. Trigger
  // PnL is already served via the trigger_update stream (which updates the
  // trigger.avg_LS_premium etc. fields read by this component).
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Snackbar helper functions
  const showSuccess = useCallback((message) => setSnackbar({ open: true, message, severity: 'success' }), []);
  const showError = useCallback((message) => setSnackbar({ open: true, message, severity: 'error' }), []);
  const showWarning = useCallback((message) => setSnackbar({ open: true, message, severity: 'warning' }), []);
  const closeSnackbar = useCallback(() => setSnackbar((prev) => ({ ...prev, open: false })), []);

  // Initialize sorting state from localStorage
  const [orderBy, setOrderBy] = useState(() => {
    const saved = localStorage.getItem('triggers_orderBy');
    return saved ? JSON.parse(saved) : null;
  });
  const [order, setOrder] = useState(() => {
    const saved = localStorage.getItem('triggers_order');
    return saved ? JSON.parse(saved) : 'asc';
  });
  // Initialize filter state from localStorage
  const [selectedPairFilters, setSelectedPairFilters] = useState(() => {
    const saved = localStorage.getItem('triggers_selectedPairFilters');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const flashTimeoutRefs = useRef(new Map());
  const notificationTimeoutRefs = useRef(new Map());
  // Ref to avoid stale closure issues with notificationsEnabled in WebSocket callbacks
  const notificationsEnabledRef = useRef(notificationsEnabled);
  const memoryStateSequenceRef = useRef(0);
  const memoryStateAppliedVersionRef = useRef(new Map());
  const [formData, setFormData] = useState({
    trading_pair: '',
    trade_mode: 'capital_krw', // 'capital_krw', 'capital_usdt', or 'quantity'
    trade_capital: '',
    trade_qty: '',
    min_order_capital: '',
    min_order_qty: '',
    max_order_capital: '',
    max_order_qty: '',
    allowed_slippage: '0.1',
    low: '',
    skip_low: false,
    high: '',
    skip_high: false,
    position_mode: 'bidirectional',
    allowed_direction: 'ls_to_sl',
    remark: '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const pendingPremiumUpdatesRef = useRef(new Map());
  const premiumFlushTimerRef = useRef(null);
  const pendingTriggerUpdatesRef = useRef(new Map());
  const triggerFlushTimerRef = useRef(null);

  const triggerIdsKey = useMemo(
    () => triggers
      .map(t => t.uuid)
      .filter(Boolean)
      .sort()
      .join(','),
    [triggers]
  );

  const nextMemoryStateVersion = useCallback(() => {
    memoryStateSequenceRef.current += 1;
    return memoryStateSequenceRef.current;
  }, []);

  const applyMemoryStatePatch = useCallback((states, sourceVersion) => {
    if (!states || typeof states !== 'object') {
      return;
    }

    setMemoryStates(prev => {
      let changed = false;
      const next = { ...prev };

      Object.entries(states).forEach(([triggerId, state]) => {
        if (!triggerId || !state) {
          return;
        }

        const appliedVersion = memoryStateAppliedVersionRef.current.get(triggerId) || 0;
        if (sourceVersion < appliedVersion) {
          return;
        }

        memoryStateAppliedVersionRef.current.set(triggerId, sourceVersion);
        if (!areMemoryStatesEqual(prev[triggerId], state)) {
          next[triggerId] = state;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, []);

  const queuePremiumUpdates = useCallback((updates) => {
    updates.forEach((update) => {
      if (!update?.trading_pair_id) {
        return;
      }
      pendingPremiumUpdatesRef.current.set(update.trading_pair_id, {
        ls_premium: update.ls_premium,
        sl_premium: update.sl_premium,
        ls_premium_converted: update.ls_premium_converted,
        sl_premium_converted: update.sl_premium_converted,
        timestamp: update.timestamp
      });
    });

    if (premiumFlushTimerRef.current) {
      return;
    }

    premiumFlushTimerRef.current = setTimeout(() => {
      premiumFlushTimerRef.current = null;
      const pending = pendingPremiumUpdatesRef.current;
      pendingPremiumUpdatesRef.current = new Map();

      setPremiumData(prev => {
        let changed = false;
        const next = { ...prev };

        pending.forEach((premium, pairId) => {
          const current = prev[pairId];
          if (
            !current ||
            current.ls_premium !== premium.ls_premium ||
            current.sl_premium !== premium.sl_premium ||
            current.timestamp !== premium.timestamp
          ) {
            next[pairId] = premium;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    }, PREMIUM_RENDER_BATCH_MS);
  }, []);

  const queueTriggerUpdates = useCallback((updates) => {
    updates.forEach((update) => {
      const triggerData = update?.data || update;
      if (triggerData?.uuid) {
        pendingTriggerUpdatesRef.current.set(triggerData.uuid, triggerData);
      }
    });

    if (triggerFlushTimerRef.current) {
      return;
    }

    triggerFlushTimerRef.current = setTimeout(() => {
      triggerFlushTimerRef.current = null;
      const batch = Array.from(pendingTriggerUpdatesRef.current.values());
      pendingTriggerUpdatesRef.current = new Map();

      if (batch.length === 1) {
        dispatch(updateSingleTrigger(batch[0]));
      } else if (batch.length > 1) {
        dispatch(updateManyTriggers(batch));
      }
    }, TRIGGER_RENDER_BATCH_MS);
  }, [dispatch]);

  // Initial fetch - WebSocket will handle real-time updates via trigger_update events
  useEffect(() => {
    dispatch(fetchTriggers(filters));
    dispatch(fetchTradingPairs({ is_active: true }));
  }, [dispatch, filters]);

  // Fallback polling only when WebSocket is disconnected
  useEffect(() => {
    if (wsConnected) {
      // WebSocket is connected, no need to poll
      return;
    }

    // WebSocket disconnected - poll as fallback until it recovers
    const interval = setInterval(() => {
      dispatch(fetchTriggers(filters));
    }, WS_FALLBACK_POLL_MS);

    return () => clearInterval(interval);
  }, [dispatch, filters, wsConnected]);

  // Persist sorting state to localStorage
  useEffect(() => {
    localStorage.setItem('triggers_orderBy', JSON.stringify(orderBy));
  }, [orderBy]);

  useEffect(() => {
    localStorage.setItem('triggers_order', JSON.stringify(order));
  }, [order]);

  // Persist filter state to localStorage
  useEffect(() => {
    localStorage.setItem('triggers_selectedPairFilters', JSON.stringify([...selectedPairFilters]));
  }, [selectedPairFilters]);

  // Keep notificationsEnabledRef in sync with state (avoids stale closures)
  useEffect(() => {
    notificationsEnabledRef.current = notificationsEnabled;
  }, [notificationsEnabled]);

  // Age-based sweep of trade-execution notifications.
  //
  // Each notification already has its own setTimeout for auto-dismiss, but
  // browsers aggressively throttle (and sometimes entirely suspend) timers
  // in background tabs. That can leave notifications visually stuck until a
  // manual refresh. This sweep runs every second and force-removes any
  // notification older than 5 seconds, matching the original timeout.
  // It also runs on visibilitychange so the cleanup fires immediately when
  // the user returns to the tab, without waiting for the next interval tick.
  const NOTIFICATION_TTL_MS = 5000;
  useEffect(() => {
    const sweep = () => {
      const now = Date.now();
      setNotifications(prev => {
        const kept = prev.filter(n => !n.addedAt || now - n.addedAt < NOTIFICATION_TTL_MS);
        // Avoid creating a new array reference when nothing changed,
        // preventing needless re-renders of the notification container.
        return kept.length === prev.length ? prev : kept;
      });
    };
    const interval = setInterval(sweep, NOTIFICATION_SWEEP_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') sweep();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Fetch wallet balances from all exchanges
  useEffect(() => {
    dispatch(fetchWalletBalances());

    // Refresh wallet balances every 5 seconds
    const balanceInterval = setInterval(() => {
      dispatch(fetchWalletBalances());
    }, 5000);

    return () => clearInterval(balanceInterval);
  }, [dispatch]);

  // Fetch memory states function - defined outside useEffect so it can be called from multiple places.
  // REST acts as reconciliation only. If a WebSocket memory update arrives after
  // this request starts, the response must not roll that trigger back.
  const fetchMemoryStates = useCallback(async (triggerIds) => {
    if (!triggerIds || triggerIds.length === 0) {
      return;
    }

    const requestVersion = nextMemoryStateVersion();

    try {
      // Fetch all memory states in one request
      const response = await api.get('/api/memory-state/', {
        params: {
          'trigger_ids[]': triggerIds
        }
      });

      applyMemoryStatePatch(response.data, requestVersion);
    } catch (error) {
      console.error('Failed to fetch memory states:', error);
    }
  }, [applyMemoryStatePatch, nextMemoryStateVersion]);

  // Fetch memory states on component mount and when triggers change
  useEffect(() => {
    if (!triggerIdsKey) {
      setMemoryStates({});
      memoryStateAppliedVersionRef.current.clear();
      return;
    }

    const triggerIds = triggerIdsKey.split(',');
    fetchMemoryStates(triggerIds);

    // Fallback polling every 30 seconds (WebSocket provides real-time updates)
    const interval = setInterval(() => {
      fetchMemoryStates(triggerIds);
    }, PAGE_REFRESH_MS);

    return () => clearInterval(interval);
  }, [fetchMemoryStates, triggerIdsKey]); // Re-run only when the visible trigger set changes.

  useEffect(() => {
    const activeIds = new Set(triggerIdsKey ? triggerIdsKey.split(',') : []);

    memoryStateAppliedVersionRef.current.forEach((_, triggerId) => {
      if (!activeIds.has(triggerId)) {
        memoryStateAppliedVersionRef.current.delete(triggerId);
      }
    });

    setMemoryStates(prev => {
      let changed = false;
      const next = {};

      Object.entries(prev).forEach(([triggerId, state]) => {
        if (activeIds.has(triggerId)) {
          next[triggerId] = state;
        } else {
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [triggerIdsKey]);

  // Set up WebSocket connection for notifications
  useEffect(() => {
    // Connect to WebSocket
    wsService.connect();

    // Listen for connection status
    const removeConnectionListener = wsService.addEventListener('connection', (data) => {
      setWsConnected(data.status === 'connected');
    });

    // Check initial connection state (in case WebSocket is already connected)
    setWsConnected(wsService.isConnected());

    // Define handleTradeExecution inside useEffect to avoid stale closure issues
    const handleTradeExecution = (execution) => {
      // Add trigger to flashing map with direction
      setFlashingTriggers(prev => {
        const newMap = new Map(prev);
        newMap.set(execution.trigger_id, execution.direction);
        return newMap;
      });

      // Remove flash after 0.1 seconds (ultra short flash)
      const flashTimeout = setTimeout(() => {
        setFlashingTriggers(prev => {
          const newMap = new Map(prev);
          newMap.delete(execution.trigger_id);
          return newMap;
        });
        flashTimeoutRefs.current.delete(execution.trigger_id);
      }, 100); // 0.1 seconds

      flashTimeoutRefs.current.set(execution.trigger_id, flashTimeout);

      // Add notification with a truly unique ID. Date.now() alone can collide
      // when the same trigger fires multiple times within the same millisecond
      // (possible in a 20Hz trading system), which produces duplicate React
      // keys and occasionally leaves a DOM node stuck. A random suffix
      // guarantees uniqueness.
      const now = Date.now();
      const notificationId = `${execution.trigger_id}-${now}-${Math.random().toString(36).slice(2, 8)}`;
      const notificationData = {
        ...execution,
        id: notificationId,
        addedAt: now, // used by the age-based sweep below
      };

      setNotifications(prev => [...prev, notificationData]);

      // Remove notification after the standard TTL (same value used by the
      // age-based sweep so the two cleanup paths agree).
      const notificationTimeout = setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        notificationTimeoutRefs.current.delete(notificationId);
      }, NOTIFICATION_TTL_MS);

      notificationTimeoutRefs.current.set(notificationId, notificationTimeout);
    };

    // Listen for trade executions - use ref to avoid re-subscription on toggle
    const removeTradeListener = wsService.addEventListener('trade_execution', (data) => {
      if (notificationsEnabledRef.current && data.trigger_id) {
        handleTradeExecution(data);
      }
    });
    
    // Listen for premium updates
    const removePremiumListener = wsService.addEventListener('premium_update', (data) => {
      if (data.trading_pair_id) {
        queuePremiumUpdates([data]);
      }
    });

    const removePremiumBatchListener = wsService.addEventListener('premium_updates', (data) => {
      if (Array.isArray(data.updates)) {
        queuePremiumUpdates(data.updates);
      }
    });
    
    // pnl_update listener removed: the data it captured was never rendered,
    // so every message caused a useless Triggers re-render. PnL values are
    // delivered via the trigger_update stream instead (trigger.avg_LS_premium,
    // trigger.realized_pnl, etc.), which this page already consumes.

    // Listen for real-time trigger updates
    const removeTriggerUpdateListener = wsService.addEventListener('trigger_update', (data) => {
      if (data.trigger_id && data.data) {
        queueTriggerUpdates([data]);
      }
    });

    const removeTriggerBatchUpdateListener = wsService.addEventListener('trigger_updates', (data) => {
      if (Array.isArray(data.updates)) {
        queueTriggerUpdates(data.updates);
      }
    });
    
    // Listen for trigger removal
    const removeTriggerRemoveListener = wsService.addEventListener('trigger_removed', (data) => {
      if (data.trigger_id) {
        // Remove the trigger from Redux store
        dispatch(removeSingleTrigger(data.trigger_id));
      }
    });

    // Listen for memory state updates (replaces polling)
    const removeMemoryStateListener = wsService.addEventListener('memory_state_update', (data) => {
      if (data.data) {
        applyMemoryStatePatch(data.data, nextMemoryStateVersion());
      }
    });

    // Start ping to keep connection alive
    wsService.startPing();

    return () => {
      removeConnectionListener();
      removeTradeListener();
      removePremiumListener();
      removePremiumBatchListener();
      removeTriggerUpdateListener();
      removeTriggerBatchUpdateListener();
      removeTriggerRemoveListener();
      removeMemoryStateListener();
      if (premiumFlushTimerRef.current) {
        clearTimeout(premiumFlushTimerRef.current);
        premiumFlushTimerRef.current = null;
      }
      if (triggerFlushTimerRef.current) {
        clearTimeout(triggerFlushTimerRef.current);
        triggerFlushTimerRef.current = null;
      }
      pendingPremiumUpdatesRef.current.clear();
      pendingTriggerUpdatesRef.current.clear();
      // Clear any remaining timeouts
      flashTimeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      notificationTimeoutRefs.current.forEach(timeout => clearTimeout(timeout));

      // Clear all notifications on unmount
      setNotifications([]);
    };
  }, [applyMemoryStatePatch, dispatch, nextMemoryStateVersion, queuePremiumUpdates, queueTriggerUpdates]);

  // Memoized handler to avoid stale closure issues
  const handleCloseNotification = useCallback((notificationId) => {
    // Clear timeout if manually closed
    const timeout = notificationTimeoutRefs.current.get(notificationId);
    if (timeout) {
      clearTimeout(timeout);
      notificationTimeoutRefs.current.delete(notificationId);
    }

    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Fetch connection status every 2 seconds
  useEffect(() => {
    const fetchConnectionStatus = async () => {
      try {
        const response = await api.get('/api/orderbook-status/active_connections/');
        const data = response.data;
        const statusMap = {};
        data.forEach(status => {
          statusMap[status.trading_pair_id] = status;
        });
        setConnectionStatuses(statusMap);
      } catch (error) {
        console.error('Failed to fetch connection status:', error);
      }
    };

    fetchConnectionStatus();
    const statusInterval = setInterval(fetchConnectionStatus, STATUS_POLL_MS);

    return () => clearInterval(statusInterval);
  }, []);

  // Stable identity for the set of trading-pair ids referenced by the current
  // triggers list. The `triggers` Redux array reference changes every time
  // trigger-pusher sends a trigger_update (~1/s per active trigger), but the
  // *set of pair ids* only changes when a trigger is added/removed or its
  // pair is re-assigned. Deriving a sorted, deduped, comma-joined string and
  // keying the polling effects off this string collapses a polling storm
  // (measured ~100 req/s) back to the intended cadence (~3-4 req/s).
  const pairIdsKey = useMemo(
    () => [...new Set(triggers.map(t => t.trading_pair))]
            .filter(id => id !== null && id !== undefined)
            .sort((a, b) => a - b)
            .join(','),
    [triggers]
  );

  // Fetch initial premium data. Premiums are kept live by the WebSocket
  // `premium_update` handler elsewhere; this effect only seeds premiumData
  // once when a pair first appears in the page.
  useEffect(() => {
    if (!pairIdsKey) return;
    const tradingPairIds = pairIdsKey.split(',').map(Number);

    const fetchInitialPremiums = async () => {
      for (const pairId of tradingPairIds) {
        try {
          const response = await fetch(`/api/premiums/?trading_pair_id=${pairId}`);
          if (response.ok) {
            const data = await response.json();
            setPremiumData(prev => ({ ...prev, [pairId]: data }));
          }
        } catch (error) {
          console.error(`Failed to fetch premiums for pair ${pairId}:`, error);
        }
      }
    };

    fetchInitialPremiums();
  }, [pairIdsKey]);

  // Fetch compact orderbook availability for all trigger pairs in one request.
  // This keeps the hot Triggers table from firing one REST request and one
  // state update per pair every STATUS_POLL_MS while premium WebSocket updates
  // are trying to render.
  useEffect(() => {
    if (!pairIdsKey) return;
    const tradingPairIds = pairIdsKey.split(',').map(Number);
    const activePairIds = new Set(tradingPairIds);

    const fetchOrderbookData = async () => {
      try {
        const response = await api.get('/api/orderbook-status/bulk_orderbooks/', {
          params: {
            trading_pair_ids: tradingPairIds.join(','),
            compact: true,
          },
        });
        const results = response.data?.results || {};

        setOrderbookData(() => {
          const next = {};
          tradingPairIds.forEach(pairId => {
            next[pairId] = results[String(pairId)] || null;
          });
          return next;
        });
      } catch (error) {
        console.error('Failed to fetch orderbook availability:', error);
        setOrderbookData(prev => {
          const next = {};
          Object.keys(prev).forEach(pairId => {
            if (activePairIds.has(parseInt(pairId))) {
              next[pairId] = prev[pairId];
            }
          });
          return next;
        });
      }
    };

    fetchOrderbookData();
    const orderbookInterval = setInterval(fetchOrderbookData, STATUS_POLL_MS);
    return () => clearInterval(orderbookInterval);
  }, [pairIdsKey]);

  const handleRefresh = async () => {
    dispatch(fetchTriggers(filters));

    if (triggerIdsKey) {
      fetchMemoryStates(triggerIdsKey.split(','));
    }
  };

  // Helper function to check if both orderbooks have data
  const hasBothOrderbookData = (tradingPairId) => {
    const data = orderbookData[tradingPairId];
    if (!data) return false;

    const hasSideData = (side) => {
      if (!side) return false;
      if (typeof side.has_data === 'boolean') return side.has_data;
      return Boolean(side.data?.bids?.length > 0 && side.data?.asks?.length > 0);
    };

    return hasSideData(data.target) && hasSideData(data.origin);
  };

  // Sort handler for Low/High columns (cycles: asc -> desc -> neutral)
  const handleRequestSort = (property) => {
    if (orderBy === property) {
      if (order === 'asc') {
        setOrder('desc');
      } else {
        // Reset to neutral (default order by created)
        setOrderBy(null);
        setOrder('asc');
      }
    } else {
      setOrderBy(property);
      setOrder('asc');
    }
  };

  // O(1) lookup of trading pair by id. Previously each row did a linear
  // .find() (O(n)) inside the render loop, which is expensive when the
  // table re-renders at high frequency with many pairs.
  const tradingPairsById = useMemo(() => {
    const map = new Map();
    for (const tp of tradingPairs) {
      map.set(tp.id, tp);
    }
    return map;
  }, [tradingPairs]);

  // Get unique trading pairs that have triggers (for filter buttons)
  // Also calculate capital usage percentage for each trading pair
  const uniqueTradingPairs = React.useMemo(() => {
    const pairs = new Map();
    triggers.forEach(trigger => {
      if (!pairs.has(trigger.trading_pair)) {
        pairs.set(trigger.trading_pair, {
          id: trigger.trading_pair,
          symbol: trigger.trading_pair_symbol,
          target_exchange_name: trigger.target_exchange_name,
          origin_exchange_name: trigger.origin_exchange_name,
          totalCapital: 0,
          enteredCapital: 0
        });
      }
      // Accumulate total trade_capital for this trading pair
      const pair = pairs.get(trigger.trading_pair);
      const triggerCapital = parseFloat(trigger.trade_capital) || 0;
      pair.totalCapital += triggerCapital;

      // Accumulate entered capital from memory states
      const memState = memoryStates[trigger.uuid];
      if (memState?.capital) {
        // Use target_entered as the primary capital indicator
        const entered = parseFloat(memState.capital.target_entered) || 0;
        pair.enteredCapital += entered;
      }
    });

    // Calculate percentage for each pair
    pairs.forEach(pair => {
      if (pair.totalCapital > 0) {
        pair.capitalPercentage = (pair.enteredCapital / pair.totalCapital) * 100;
      } else {
        pair.capitalPercentage = 0;
      }
    });

    // Sort by symbol name
    return Array.from(pairs.values()).sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [triggers, memoryStates]);

  // Handle filter toggle
  const handleFilterToggle = (pairId) => {
    setSelectedPairFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pairId)) {
        newSet.delete(pairId);
      } else {
        newSet.add(pairId);
      }
      return newSet;
    });
  };

  // Handle ALL button click - clear all filters
  const handleSelectAll = () => {
    setSelectedPairFilters(new Set());
  };

  // Get sorted and filtered triggers
  // Memoized filter+sort. Previously recomputed on every parent re-render
  // (up to 200+/s during high-frequency premium_update), which was a
  // dominant source of CPU churn. Now only recomputes when the inputs
  // that actually affect the ordering change.
  const sortedTriggers = useMemo(() => {
    // First apply filter
    let filtered = triggers;
    if (selectedPairFilters.size > 0) {
      filtered = triggers.filter(t => selectedPairFilters.has(t.trading_pair));
    }

    // Then sort
    if (!orderBy) {
      // Default sort by created_at descending (newest first)
      return [...filtered].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
    }

    return [...filtered].sort((a, b) => {
      const aValue = parseFloat(a[orderBy]) || 0;
      const bValue = parseFloat(b[orderBy]) || 0;

      if (order === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  }, [triggers, selectedPairFilters, orderBy, order]);

  const handleToggleStatus = useCallback(async (triggerId) => {
    try {
      await dispatch(toggleTriggerStatus(triggerId)).unwrap();
      // Refresh the list after status toggle
      dispatch(fetchTriggers(filters));
    } catch (error) {
      console.error('Failed to toggle trigger status:', error);
    }
  }, [dispatch, filters]);

  // Per-trigger skip_low/skip_high toggles used by TriggerRow. Declared
  // here so the row (React.memo) receives stable references.
  const handleToggleSkipLow = useCallback(async (trigger) => {
    try {
      await dispatch(updateTrigger({
        id: trigger.uuid,
        data: { skip_low: !trigger.skip_low }
      })).unwrap();
      dispatch(fetchTriggers(filters));
    } catch (error) {
      console.error('Failed to toggle skip_low:', error);
    }
  }, [dispatch, filters]);

  const handleToggleSkipHigh = useCallback(async (trigger) => {
    try {
      await dispatch(updateTrigger({
        id: trigger.uuid,
        data: { skip_high: !trigger.skip_high }
      })).unwrap();
      dispatch(fetchTriggers(filters));
    } catch (error) {
      console.error('Failed to toggle skip_high:', error);
    }
  }, [dispatch, filters]);

  // Emergency stop - pause all active triggers in the filtered list
  const handleEmergencyStop = async () => {
    const activeTriggers = sortedTriggers.filter(t => t.status === 'active');
    if (activeTriggers.length === 0) {
      return;
    }

    const ok = await confirm({
      title: 'Stop active triggers?',
      message: `This will pause ${activeTriggers.length} active trigger${activeTriggers.length > 1 ? 's' : ''} in the current view. They can be re-activated individually afterwards.`,
      confirmLabel: 'Stop all',
      cancelLabel: 'Cancel',
      severity: 'warning',
    });
    if (!ok) return;

    try {
      // Stop all active triggers in parallel
      await Promise.all(
        activeTriggers.map(trigger =>
          dispatch(toggleTriggerStatus(trigger.uuid)).unwrap()
        )
      );
      // Refresh the list after stopping
      dispatch(fetchTriggers(filters));
    } catch (error) {
      console.error('Failed to stop triggers:', error);
    }
  };

  const handleDelete = useCallback(async (triggerId) => {
    const ok = await confirm({
      title: 'Delete trigger?',
      message: 'This permanently removes the trigger. Historical trade and PnL records remain intact.',
      confirmLabel: 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await dispatch(deleteTrigger(triggerId)).unwrap();
      // Refresh the list after deletion
      dispatch(fetchTriggers(filters));
    } catch (error) {
      console.error('Failed to delete trigger:', error);
    }
  }, [dispatch, filters, confirm]);

  const handleOpenManualOrder = useCallback((trigger) => {
    setSelectedTrigger(trigger);

    // Calculate default sides based on current positions
    const targetPosition = trigger.target_position || 0;
    const originPosition = trigger.origin_position || 0;

    // If position > 0 (LONG), default to SELL (SHORT)
    // If position < 0 (SHORT), default to BUY (LONG)
    // If position = 0, default to BUY (LONG)
    const targetDefaultSide = targetPosition > 0 ? 'sell' : 'buy';
    const originDefaultSide = originPosition > 0 ? 'sell' : 'buy';

    setManualOrderData({
      target_quantity: 0,
      target_side: targetDefaultSide,
      origin_quantity: 0,
      origin_side: originDefaultSide,
      track_position: false
    });

    setManualOrderDialogOpen(true);
  }, []);

  const handleCloseManualOrder = () => {
    setManualOrderDialogOpen(false);
    setSelectedTrigger(null);
    setManualOrderData({
      target_quantity: 0,
      target_side: 'buy',
      origin_quantity: 0,
      origin_side: 'buy',
      track_position: false
    });
  };

  // Helper to extract number from formatted string like "123,456 원" or "123.45 USDT"
  const extractNumber = (str) => {
    if (typeof str === 'number') return str;
    if (typeof str !== 'string') return 0;
    // Remove commas and any non-numeric characters except decimal point
    const cleaned = str.replace(/,/g, '').replace(/[^\d.-]/g, '');
    return parseFloat(cleaned) || 0;
  };

  // Calculate aggregated capital data for a trading pair
  const getCapitalDetailForTradingPair = (tradingPairId) => {
    // Find all triggers for this trading pair
    const pairTriggers = triggers.filter(t => t.trading_pair === tradingPairId);
    const tradingPair = tradingPairs.find(tp => tp.id === tradingPairId);

    // Aggregate capital data
    let totalCapitalCap = 0;
    let totalMemoryTargetEntered = 0;
    let totalMemoryOriginEntered = 0;
    let totalDbTargetEntered = 0;
    let totalDbOriginEntered = 0;

    pairTriggers.forEach(trigger => {
      // Sum trade_capital (cap)
      totalCapitalCap += parseFloat(trigger.trade_capital) || 0;

      // Sum memory entered capital
      const memState = memoryStates[trigger.uuid];
      if (memState?.capital) {
        totalMemoryTargetEntered += parseFloat(memState.capital.target_entered) || 0;
        totalMemoryOriginEntered += parseFloat(memState.capital.origin_entered) || 0;
      }

      // Sum DB entered capital from entered_trade_capital field
      // The serializer returns an object like: { target: "123,456 원", origin: "234,567 원", display: "..." }
      if (trigger.entered_trade_capital) {
        if (typeof trigger.entered_trade_capital === 'object' && trigger.entered_trade_capital.target !== undefined) {
          // Object format with formatted strings - extract numbers
          totalDbTargetEntered += extractNumber(trigger.entered_trade_capital.target);
          totalDbOriginEntered += extractNumber(trigger.entered_trade_capital.origin);
        } else if (typeof trigger.entered_trade_capital === 'string' && trigger.entered_trade_capital.includes('/')) {
          // Legacy string format "123456/234567"
          const parts = trigger.entered_trade_capital.split('/');
          totalDbTargetEntered += extractNumber(parts[0]);
          totalDbOriginEntered += extractNumber(parts[1]);
        } else if (typeof trigger.entered_trade_capital === 'number') {
          // Direct number
          totalDbTargetEntered += trigger.entered_trade_capital;
        }
      }
    });

    // Separate units for target and origin exchanges
    const targetUnit = tradingPair?.target_quote_asset === 'KRW' ? '원' : (tradingPair?.target_quote_asset || 'USDT');
    const originUnit = tradingPair?.origin_quote_asset === 'KRW' ? '원' : (tradingPair?.origin_quote_asset || 'USDT');
    // Cap unit follows target (since trade_capital is typically in target currency)
    const capUnit = targetUnit;

    return {
      tradingPair,
      triggerCount: pairTriggers.length,
      totalCapitalCap,
      totalMemoryTargetEntered,
      totalMemoryOriginEntered,
      totalDbTargetEntered,
      totalDbOriginEntered,
      targetUnit,
      originUnit,
      capUnit,
      memoryUsagePercent: totalCapitalCap > 0 ? (totalMemoryTargetEntered / totalCapitalCap) * 100 : 0,
      dbUsagePercent: totalCapitalCap > 0 ? (totalDbTargetEntered / totalCapitalCap) * 100 : 0
    };
  };

  const handleOpenCapitalDetail = useCallback((tradingPairId) => {
    setSelectedCapitalTradingPair(tradingPairId);
    setCapitalDetailOpen(true);
  }, []);

  const handleCloseCapitalDetail = () => {
    setCapitalDetailOpen(false);
    setSelectedCapitalTradingPair(null);
  };

  // Navigate to Trades page with PnL History tab and trigger filter
  const handleCreatedClick = useCallback((trigger) => {
    navigate('/trades', {
      state: {
        tabValue: 0, // PnL History tab (index 0)
        selectedTrigger: {
          uuid: trigger.uuid,
          symbol: trigger.trading_pair_symbol,
          trading_pair: trigger.trading_pair
        }
      }
    });
  }, [navigate]);

  const handleExecuteManualOrder = async () => {
    if (!selectedTrigger) return;

    const targetQty = parseFloat(manualOrderData.target_quantity) || 0;
    const originQty = parseFloat(manualOrderData.origin_quantity) || 0;

    if (targetQty < 0 || originQty < 0) {
      showError('Quantity must be >= 0');
      return;
    }

    if (targetQty === 0 && originQty === 0) {
      showError('At least one exchange must have quantity > 0');
      return;
    }

    setManualOrderLoading(true);

    try {
      const response = await api.post('/api/orders/manual-position/', {
        trigger_uuid: selectedTrigger.uuid,
        target_quantity: targetQty,
        target_side: manualOrderData.target_side,
        origin_quantity: originQty,
        origin_side: manualOrderData.origin_side,
        track_position: manualOrderData.track_position
      });

      if (response.data.success) {
        const trackingMsg = manualOrderData.track_position
          ? ' Trades recorded in DB and PnL will be recalculated.'
          : '';
        showSuccess(`Manual orders executed successfully!${trackingMsg}`);
        handleCloseManualOrder();
        // Refresh triggers to show updated data
        dispatch(fetchTriggers(filters));
      } else {
        const errors = [];
        if (response.data.results.target?.error) {
          errors.push(`Target: ${response.data.results.target.error}`);
        }
        if (response.data.results.origin?.error) {
          errors.push(`Origin: ${response.data.results.origin.error}`);
        }
        showError(`Manual order errors: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Failed to execute manual order:', error);
      showError(`Failed to execute manual order: ${error.response?.data?.error || error.message}`);
    } finally {
      setManualOrderLoading(false);
    }
  };

  const handleToggleExpand = useCallback((trigger) => {
    // Functional setState so this callback has no expandedRow dep and
    // stays stable across unrelated re-renders (TriggerRow memo).
    setExpandedRow((curr) => {
      if (curr === trigger.uuid) {
        setEditingData({});
        return null;
      }
      const determineTradeMode = () => {
        if (trigger.trade_qty) return 'quantity';
        if (trigger.currency === 'USDT') return 'capital_usdt';
        return 'capital_krw';
      };
      setEditingData({
        [trigger.uuid]: {
          trade_mode: determineTradeMode(),
          trade_capital: trigger.trade_capital || '',
          trade_qty: trigger.trade_qty || '',
          currency: trigger.currency,
          min_order_capital: trigger.min_order_capital || '',
          min_order_qty: trigger.min_order_qty || '',
          max_order_capital: trigger.max_order_capital || '',
          max_order_qty: trigger.max_order_qty || '',
          allowed_slippage: Number(trigger.allowed_slippage) * 100,
          low: formatDecimal(trigger.low),
          skip_low: trigger.skip_low,
          high: formatDecimal(trigger.high),
          skip_high: trigger.skip_high,
          position_mode: trigger.position_mode || 'bidirectional',
          allowed_direction: trigger.allowed_direction || 'ls_to_sl',
          remark: trigger.remark || '',
        }
      });
      return trigger.uuid;
    });
  }, []);

  const handleOpenDialog = () => {
    setFormData({
      trading_pair: '',
      trade_mode: 'capital_krw',
      trade_capital: '',
      trade_qty: '',
      min_order_capital: '',
      min_order_qty: '',
      max_order_capital: '',
      max_order_qty: '',
      allowed_slippage: '0.1',
      low: '',
      skip_low: false,
      high: '',
      skip_high: false,
      position_mode: 'bidirectional',
      allowed_direction: 'ls_to_sl',
      remark: '',
    });
    setValidationErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleOpenGridDialog = useCallback(() => {
    setGridDialogOpen(true);
  }, []);

  const handleCloseGridDialog = useCallback(() => {
    setGridDialogOpen(false);
  }, []);

  const handleGridPreview = useCallback((payload) => {
    return dispatch(createTriggerGrid(payload)).unwrap();
  }, [dispatch]);

  const handleGridCreate = useCallback((payload) => {
    return dispatch(createTriggerGrid(payload)).unwrap();
  }, [dispatch]);

  const handleGridCreated = useCallback(() => {
    dispatch(fetchTriggers(filters));
  }, [dispatch, filters]);

  // Real-time validation function
  const validateForm = (data) => {
    const errors = {};

    // Extract numeric values
    const lowValue = Number(data.low);
    const highValue = Number(data.high);
    const tradeCapital = Number(data.trade_capital);
    const minOrderCapital = Number(data.min_order_capital);
    const maxOrderCapital = Number(data.max_order_capital);

    // Validation 1: Low must be less than High (unless skipped)
    if (!data.skip_low && !data.skip_high && data.low && data.high && lowValue >= highValue) {
      errors.low = 'Low must be less than High';
      errors.high = 'High must be greater than Low';
    }

    // Validation 2 & 3: Capital validations (only for capital mode)
    if (data.trade_mode.startsWith('capital_')) {
      // Validation 2: Min Order Capital ≤ Max Order Capital
      if (minOrderCapital && maxOrderCapital && minOrderCapital > maxOrderCapital) {
        errors.min_order_capital = 'Min must not exceed Max';
        errors.max_order_capital = 'Max must not be less than Min';
      }

      // Validation 3: Max Order Capital ≤ Trade Capital
      if (maxOrderCapital && tradeCapital && maxOrderCapital > tradeCapital) {
        errors.max_order_capital = 'Max must not exceed Trade Capital';
        errors.trade_capital = 'Trade Capital must not be less than Max Order Capital';
      }
    }

    return errors;
  };

  const handleFormChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;

    let updatedData;

    // Special handling for trading_pair selection - auto-select currency based on quote assets
    if (field === 'trading_pair') {
      const selectedPair = tradingPairs.find(p => p.id === value);

      if (selectedPair) {
        // Auto-select currency: KRW if either exchange uses KRW, otherwise USDT
        const hasKRW = selectedPair.target_quote_asset === 'KRW' ||
                       selectedPair.origin_quote_asset === 'KRW';
        const defaultMode = hasKRW ? 'capital_krw' : 'capital_usdt';

        updatedData = {
          ...formData,
          [field]: value,
          trade_mode: defaultMode  // Auto-select based on pair's quote assets
        };
      } else {
        updatedData = { ...formData, [field]: value };
      }
    } else {
      updatedData = { ...formData, [field]: value };
    }

    // Run validation on updated data
    const errors = validateForm(updatedData);
    setValidationErrors(errors);
    setFormData(updatedData);
  };

  const handleEditingChange = useCallback((triggerId, field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setEditingData((prev) => ({
      ...prev,
      [triggerId]: {
        ...prev[triggerId],
        [field]: value
      }
    }));
  }, []);

  const handleSubmit = async () => {
    try {
      // Extract numeric values for validation
      const lowValue = Number(formData.low);
      const highValue = Number(formData.high);
      const tradeCapital = Number(formData.trade_capital);
      const minOrderCapital = Number(formData.min_order_capital);
      const maxOrderCapital = Number(formData.max_order_capital);

      // Validation 1: Low must be less than High (unless skipped)
      if (!formData.skip_low && !formData.skip_high && lowValue >= highValue) {
        showWarning('Low value must be less than High value');
        return;
      }

      // Validation 2 & 3: Capital validations (only for capital mode)
      if (formData.trade_mode.startsWith('capital_')) {
        // Validation 2: Min Order Capital ≤ Max Order Capital
        if (minOrderCapital && maxOrderCapital && minOrderCapital > maxOrderCapital) {
          showWarning('Min Order Capital must not be greater than Max Order Capital');
          return;
        }

        // Validation 3: Max Order Capital ≤ Trade Capital
        if (maxOrderCapital && tradeCapital && maxOrderCapital > tradeCapital) {
          showWarning('Max Order Capital must not be greater than Trade Capital');
          return;
        }
      }

      // Derive currency from trade_mode
      const currency = formData.trade_mode === 'capital_krw' ? 'KRW' :
                       formData.trade_mode === 'capital_usdt' ? 'USDT' :
                       'KRW';  // Fallback for quantity mode

      // Prepare data with allowed_slippage divided by 100
      const submitData = {
        trading_pair: formData.trading_pair,
        currency: currency,
        allowed_slippage: Number(formData.allowed_slippage) / 100,
        low: formData.low,
        skip_low: formData.skip_low,
        high: formData.high,
        skip_high: formData.skip_high,
        position_mode: formData.position_mode,
        allowed_direction: formData.allowed_direction,
        remark: formData.remark,
      };

      // Add either capital or quantity fields based on trade_mode
      if (formData.trade_mode.startsWith('capital_')) {
        submitData.trade_capital = formData.trade_capital;
        submitData.min_order_capital = formData.min_order_capital;
        submitData.max_order_capital = formData.max_order_capital;
      } else {
        submitData.trade_qty = formData.trade_qty;
        submitData.min_order_qty = formData.min_order_qty;
        submitData.max_order_qty = formData.max_order_qty;
      }
      
      await dispatch(createTrigger(submitData)).unwrap();
      handleCloseDialog();
      // Refresh the list after create/update
      dispatch(fetchTriggers(filters));
    } catch (error) {
      console.error('Failed to save trigger:', error);
    }
  };

  // Accepts the row's current form data so the callback is stable
  // (no editingData dep). The row already has editing as a prop.
  const handleSaveInlineEdit = useCallback(async (triggerId, formData) => {
    try {

      // Extract numeric values for validation
      const lowValue = Number(formData.low);
      const highValue = Number(formData.high);
      const tradeCapital = Number(formData.trade_capital);
      const minOrderCapital = Number(formData.min_order_capital);
      const maxOrderCapital = Number(formData.max_order_capital);

      // Validation 1: Low must be less than High (unless skipped)
      if (!formData.skip_low && !formData.skip_high && lowValue >= highValue) {
        showWarning('Low value must be less than High value');
        return;
      }

      // Validation 2 & 3: Capital validations (only for capital mode)
      if (formData.trade_mode && formData.trade_mode.startsWith('capital_')) {
        // Validation 2: Min Order Capital ≤ Max Order Capital
        if (minOrderCapital && maxOrderCapital && minOrderCapital > maxOrderCapital) {
          showWarning('Min Order Capital must not be greater than Max Order Capital');
          return;
        }

        // Validation 3: Max Order Capital ≤ Trade Capital
        if (maxOrderCapital && tradeCapital && maxOrderCapital > tradeCapital) {
          showWarning('Max Order Capital must not be greater than Trade Capital');
          return;
        }
      }

      // Derive currency from trade_mode if available
      const currency = formData.trade_mode === 'capital_krw' ? 'KRW' :
                       formData.trade_mode === 'capital_usdt' ? 'USDT' :
                       formData.currency || 'KRW';  // Fallback to existing currency

      // Prepare data with allowed_slippage divided by 100
      const submitData = {
        currency: currency,
        allowed_slippage: Number(formData.allowed_slippage) / 100,
        low: formData.low,
        skip_low: formData.skip_low,
        high: formData.high,
        skip_high: formData.skip_high,
        position_mode: formData.position_mode,
        allowed_direction: formData.allowed_direction,
        remark: formData.remark,
      };

      // Add either capital or quantity fields based on trade_mode
      // For existing triggers, trade_mode is determined from which fields have values
      if (formData.trade_mode && formData.trade_mode.startsWith('capital_')) {
        submitData.trade_capital = formData.trade_capital;
        submitData.min_order_capital = formData.min_order_capital;
        submitData.max_order_capital = formData.max_order_capital;
      } else if (formData.trade_mode === 'quantity') {
        submitData.trade_qty = formData.trade_qty;
        submitData.min_order_qty = formData.min_order_qty;
        submitData.max_order_qty = formData.max_order_qty;
      } else {
        // Fallback: determine based on which fields exist
        if (formData.trade_capital) {
          submitData.trade_capital = formData.trade_capital;
          submitData.min_order_capital = formData.min_order_capital;
          submitData.max_order_capital = formData.max_order_capital;
        } else {
          submitData.trade_qty = formData.trade_qty;
          submitData.min_order_qty = formData.min_order_qty;
          submitData.max_order_qty = formData.max_order_qty;
        }
      }
      
      await dispatch(updateTrigger({ id: triggerId, data: submitData })).unwrap();
      setExpandedRow(null);
      setEditingData({});
      // Refresh the list after update
      dispatch(fetchTriggers(filters));
    } catch (error) {
      console.error('Failed to update trigger:', error);
    }
  }, [dispatch, filters, showWarning]);

  const handleCancelEdit = useCallback(() => {
    setExpandedRow(null);
    setEditingData({});
  }, []);

  // Group wallet balances by exchange+market_type for display
  const groupedBalances = React.useMemo(() => {
    if (!walletBalances || walletBalances.length === 0) return [];

    const grouped = {};
    walletBalances.forEach(balance => {
      const key = `${balance.exchange}_${balance.market_type}`;
      if (!grouped[key]) {
        grouped[key] = {
          exchange: balance.exchange,
          market_type: balance.market_type,
          assets: []
        };
      }
      grouped[key].assets.push({
        asset: balance.asset,
        free: balance.free,
        error: balance.error
      });
    });

    return Object.values(grouped);
  }, [walletBalances]);

  // Get market type short label
  const getMarketTypeLabel = (marketType) => {
    switch (marketType) {
      case 'USD_M': return 'F';  // Futures
      case 'SPOT': return 'S';   // Spot
      case 'COIN_M': return 'C'; // Coin-M
      default: return marketType;
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: isMobile ? 0.5 : 0 }}>
      <Paper sx={{ p: isMobile ? 1 : 2, mb: isMobile ? 1 : 2 }}>
        {/* Row 1: New Trigger button and controls */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1}>
            <Button
              variant="contained"
              startIcon={!isMobile && <Add />}
              onClick={() => handleOpenDialog()}
              size={isMobile ? 'small' : 'medium'}
              sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
            >
              {isMobile ? '+ New' : 'New Trigger'}
            </Button>
            <Button
              variant="outlined"
              startIcon={!isMobile && <Add />}
              onClick={handleOpenGridDialog}
              size={isMobile ? 'small' : 'medium'}
              sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
            >
              {isMobile ? 'Grid' : 'Grid Create'}
            </Button>
          </Box>
          <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1}>
            {/* WebSocket Status */}
            <Chip
              label={wsConnected ? 'Live' : 'Offline'}
              color={wsConnected ? 'success' : 'error'}
              size="small"
              sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : undefined }}
            />

            {/* Notification Toggle */}
            <Tooltip title={notificationsEnabled ? 'Disable notifications' : 'Enable notifications'}>
              <IconButton
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                color={notificationsEnabled ? 'primary' : 'default'}
                size="small"
                sx={{ p: isMobile ? 0.5 : 1 }}
                aria-label={notificationsEnabled ? 'Disable trade notifications' : 'Enable trade notifications'}
                aria-pressed={notificationsEnabled}
              >
                {notificationsEnabled ? <Notifications sx={{ fontSize: isMobile ? 18 : 24 }} /> : <NotificationsOff sx={{ fontSize: isMobile ? 18 : 24 }} />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ p: isMobile ? 0.5 : 1 }} aria-label="Refresh triggers">
                <Refresh sx={{ fontSize: isMobile ? 18 : 24 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Row 2: Wallet Balances */}
        {groupedBalances.length > 0 && (
          <Box
            display="flex"
            flexWrap="wrap"
            gap={isMobile ? 1 : 2}
            sx={{
              pt: 1,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            {groupedBalances.map((group) => (
              <Box
                key={`${group.exchange}_${group.market_type}`}
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  bgcolor: 'background.default',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  minWidth: isMobile ? 'auto' : 120
                }}
              >
                <ExchangeIcon exchangeName={group.exchange} size={isMobile ? 14 : 16} />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: isMobile ? '0.6rem' : '0.75rem',
                    fontWeight: 500,
                    color: 'text.secondary'
                  }}
                >
                  ({getMarketTypeLabel(group.market_type)}):
                </Typography>
                {group.assets.map((asset, idx) => (
                  <Typography
                    key={asset.asset}
                    variant="caption"
                    sx={{
                      fontSize: isMobile ? '0.6rem' : '0.75rem',
                      fontWeight: 600,
                      color: asset.error ? 'error.main' : 'text.primary'
                    }}
                  >
                    {asset.error ? 'N/A' : (
                      asset.asset === 'KRW'
                        ? `₩${formatCompactNumber(asset.free)}`
                        : `$${formatCompactNumber(asset.free)}`
                    )}
                    {asset.asset !== 'KRW' && !asset.error && (
                      <span style={{ fontWeight: 400, opacity: 0.7 }}> {asset.asset}</span>
                    )}
                    {idx < group.assets.length - 1 && ' / '}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Trading Pair Filter */}
      {uniqueTradingPairs.length > 0 && (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={isMobile ? 1 : 1.5}
          sx={{ mb: 2 }}
        >
          {/* ALL Button */}
          <Box
            onClick={handleSelectAll}
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              bgcolor: selectedPairFilters.size === 0 ? 'primary.main' : 'background.paper',
              color: selectedPairFilters.size === 0 ? 'primary.contrastText' : 'text.primary',
              px: isMobile ? 1 : 1.5,
              py: isMobile ? 0.5 : 0.75,
              borderRadius: 1,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: selectedPairFilters.size === 0 ? 'primary.main' : 'divider',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: selectedPairFilters.size === 0 ? 'primary.dark' : 'action.hover'
              }
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: isMobile ? '0.65rem' : '0.8rem',
                fontWeight: 600
              }}
            >
              ALL
            </Typography>
          </Box>

          {/* Trading Pair Buttons */}
          {uniqueTradingPairs.map(pair => {
            const isSelected = selectedPairFilters.has(pair.id);
            const percentage = Math.min(100, Math.max(0, pair.capitalPercentage || 0));
            return (
              <Box
                key={pair.id}
                onClick={() => handleFilterToggle(pair.id)}
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  bgcolor: isSelected ? 'primary.main' : 'background.paper',
                  color: isSelected ? 'primary.contrastText' : 'text.primary',
                  px: isMobile ? 0.75 : 1,
                  py: isMobile ? 0.5 : 0.75,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: isSelected ? 'primary.dark' : 'action.hover'
                  },
                  // Capital usage fill from left to right (green)
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${percentage}%`,
                    bgcolor: isSelected ? 'rgba(76, 175, 80, 0.5)' : 'rgba(76, 175, 80, 0.35)',
                    transition: 'width 0.3s ease-in-out',
                    zIndex: 0
                  }
                }}
              >
                <CryptoIcon symbol={pair.target_symbol || pair.symbol} size={isMobile ? 14 : 18} style={{ position: 'relative', zIndex: 1 }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: isMobile ? '0.65rem' : '0.8rem',
                    fontWeight: 600,
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {pair.symbol}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.25} sx={{ ml: 0.5, position: 'relative', zIndex: 1 }}>
                  <ExchangeIcon exchangeName={pair.target_exchange_name} size={isMobile ? 12 : 14} />
                  <Typography variant="caption" sx={{ fontSize: isMobile ? '0.55rem' : '0.7rem', opacity: 0.7 }}>↔</Typography>
                  <ExchangeIcon exchangeName={pair.origin_exchange_name} size={isMobile ? 12 : 14} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <TableContainer component={Paper} sx={{ width: '100%' }}>
        <Table sx={{ minWidth: isMobile ? 0 : 650 }} size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ px: isMobile ? 0.5 : 2, py: isMobile ? 0.5 : 1, fontSize: isMobile ? '0.7rem' : undefined }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  Actions
                  <Tooltip title={`Stop all ${sortedTriggers.filter(t => t.status === 'active').length} active triggers`}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleEmergencyStop}
                        disabled={sortedTriggers.filter(t => t.status === 'active').length === 0}
                        sx={{
                          p: 0.25,
                          color: 'error.main',
                          '&:hover': {
                            bgcolor: 'error.light',
                            color: 'error.contrastText'
                          }
                        }}
                      >
                        <StopCircle sx={{ fontSize: isMobile ? 16 : 20 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell sx={{ px: isMobile ? 0.5 : 2, py: isMobile ? 0.5 : 1, fontSize: isMobile ? '0.7rem' : undefined }}>Pair</TableCell>
              <TableCell sx={{ px: isMobile ? 0.5 : 2, py: isMobile ? 0.5 : 1, fontSize: isMobile ? '0.7rem' : undefined }} sortDirection={orderBy === 'low' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'low'}
                  direction={orderBy === 'low' ? order : 'asc'}
                  onClick={() => handleRequestSort('low')}
                >
                  Low
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ px: isMobile ? 0.5 : 2, py: isMobile ? 0.5 : 1, fontSize: isMobile ? '0.7rem' : undefined }} sortDirection={orderBy === 'high' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'high'}
                  direction={orderBy === 'high' ? order : 'asc'}
                  onClick={() => handleRequestSort('high')}
                >
                  High
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ px: isMobile ? 0.5 : 2, py: isMobile ? 0.5 : 1, fontSize: isMobile ? '0.7rem' : undefined }}>Capital</TableCell>
              <TableCell sx={{ px: isMobile ? 0.5 : 2, py: isMobile ? 0.5 : 1, fontSize: isMobile ? '0.7rem' : undefined }}>Pos</TableCell>
              <TableCell sx={{ px: isMobile ? 0.5 : 2, py: isMobile ? 0.5 : 1, fontSize: isMobile ? '0.7rem' : undefined }}>Pnl</TableCell>
              <TableCell sx={{ px: isMobile ? 0.5 : 2, py: isMobile ? 0.5 : 1, fontSize: isMobile ? '0.7rem' : undefined }}>Status</TableCell>
              <TableCell sx={{ px: isMobile ? 0.5 : 2, py: isMobile ? 0.5 : 1, fontSize: isMobile ? '0.7rem' : undefined }}>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTriggers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No triggers found
                </TableCell>
              </TableRow>
            ) : (
              sortedTriggers.map((trigger) => (
                <TriggerRow
                  key={trigger.uuid}
                  trigger={trigger}
                  hasData={hasBothOrderbookData(trigger.trading_pair)}
                  connStatus={connectionStatuses[trigger.trading_pair]}
                  isPairActive={tradingPairsById.get(trigger.trading_pair)?.is_active || false}
                  isMobile={isMobile}
                  isExpanded={expandedRow === trigger.uuid}
                  isFlashing={flashingTriggers.has(trigger.uuid)}
                  flashDirection={flashingTriggers.get(trigger.uuid)}
                  premium={premiumData[trigger.trading_pair]}
                  memState={memoryStates[trigger.uuid]}
                  editing={editingData[trigger.uuid]}
                  onToggleExpand={handleToggleExpand}
                  onToggleStatus={handleToggleStatus}
                  onDelete={handleDelete}
                  onOpenCapitalDetail={handleOpenCapitalDetail}
                  onOpenManualOrder={handleOpenManualOrder}
                  onCreatedClick={handleCreatedClick}
                  onEditingChange={handleEditingChange}
                  onCancelEdit={handleCancelEdit}
                  onSaveInlineEdit={handleSaveInlineEdit}
                  onToggleSkipLow={handleToggleSkipLow}
                  onToggleSkipHigh={handleToggleSkipHigh}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TriggerGridCreateDialog
        open={gridDialogOpen}
        onClose={handleCloseGridDialog}
        tradingPairs={tradingPairs}
        onPreview={handleGridPreview}
        onCreate={handleGridCreate}
        onCreated={handleGridCreated}
        showSuccess={showSuccess}
        showError={showError}
        confirm={confirm}
      />

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create New Trigger
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              select
              fullWidth
              label="Trading Pair"
              value={formData.trading_pair}
              onChange={handleFormChange('trading_pair')}
              margin="normal"
              required
            >
              {tradingPairs
                .filter(pair => pair.is_active)
                .map((pair) => (
                <MenuItem key={pair.id} value={pair.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CryptoIcon symbol={pair.target_symbol || pair.symbol} size={20} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {pair.symbol}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                        <ExchangeIcon exchangeName={pair.target_exchange_name} size={14} />
                        <Typography variant="caption" color="textSecondary">
                          Target: {pair.target_exchange_name} - {pair.target_market_type} ({pair.target_quote_asset})
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ExchangeIcon exchangeName={pair.origin_exchange_name} size={14} />
                        <Typography variant="caption" color="textSecondary">
                          Origin: {pair.origin_exchange_name} - {pair.origin_market_type} ({pair.origin_quote_asset})
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Trade Mode
              </Typography>
              <RadioGroup
                row
                value={formData.trade_mode}
                onChange={handleFormChange('trade_mode')}
              >
                <FormControlLabel
                  value="capital_krw"
                  control={<Radio />}
                  label="KRW (원)"
                />
                <FormControlLabel
                  value="capital_usdt"
                  control={<Radio />}
                  label="USDT"
                />
                <FormControlLabel
                  value="quantity"
                  control={<Radio />}
                  label="Qty (미구현)"
                  disabled
                />
              </RadioGroup>
            </Box>

            <Box display="flex" gap={2}>
              <SignedNumberField
                fullWidth
                label="Low (%)"
                value={formData.low}
                onChange={handleFormChange('low')}
                margin="normal"
                required
                error={!!validationErrors.low}
                helperText={validationErrors.low}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.skip_low}
                    onChange={handleFormChange('skip_low')}
                  />
                }
                label="Skip Low"
                sx={{ mt: 2 }}
              />
            </Box>

            <Box display="flex" gap={2}>
              <SignedNumberField
                fullWidth
                label="High (%)"
                value={formData.high}
                onChange={handleFormChange('high')}
                margin="normal"
                required
                error={!!validationErrors.high}
                helperText={validationErrors.high}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.skip_high}
                    onChange={handleFormChange('skip_high')}
                  />
                }
                label="Skip High"
                sx={{ mt: 2 }}
              />
            </Box>

            <TextField
              fullWidth
              label={formData.trade_mode === 'capital_krw' ? "Trade Capital (원)" :
                     formData.trade_mode === 'capital_usdt' ? "Trade Capital (USDT)" :
                     "Trade Quantity"}
              value={formData.trade_mode.startsWith('capital_') ? formatWithCommas(formData.trade_capital) : formData.trade_qty}
              onChange={(e) => {
                const rawValue = removeCommas(e.target.value);
                if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                  handleFormChange(formData.trade_mode.startsWith('capital_') ? 'trade_capital' : 'trade_qty')({ target: { value: rawValue } });
                }
              }}
              margin="normal"
              error={!!validationErrors.trade_capital}
              helperText={validationErrors.trade_capital || (formData.trade_mode === 'quantity' ? `Unit: ${formData.trading_pair ? tradingPairs.find(p => p.id === formData.trading_pair)?.symbol || '' : ''}` : '')}
            />

            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label={formData.trade_mode === 'capital_krw' ? "Min Order Capital (원)" :
                       formData.trade_mode === 'capital_usdt' ? "Min Order Capital (USDT)" :
                       "Min Order Quantity"}
                value={formData.trade_mode.startsWith('capital_') ? formatWithCommas(formData.min_order_capital) : formData.min_order_qty}
                onChange={(e) => {
                  const rawValue = removeCommas(e.target.value);
                  if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                    handleFormChange(formData.trade_mode.startsWith('capital_') ? 'min_order_capital' : 'min_order_qty')({ target: { value: rawValue } });
                  }
                }}
                margin="normal"
                error={!!validationErrors.min_order_capital}
                helperText={validationErrors.min_order_capital}
              />
              <TextField
                fullWidth
                label={formData.trade_mode === 'capital_krw' ? "Max Order Capital (원)" :
                       formData.trade_mode === 'capital_usdt' ? "Max Order Capital (USDT)" :
                       "Max Order Quantity"}
                value={formData.trade_mode.startsWith('capital_') ? formatWithCommas(formData.max_order_capital) : formData.max_order_qty}
                onChange={(e) => {
                  const rawValue = removeCommas(e.target.value);
                  if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                    handleFormChange(formData.trade_mode.startsWith('capital_') ? 'max_order_capital' : 'max_order_qty')({ target: { value: rawValue } });
                  }
                }}
                margin="normal"
                error={!!validationErrors.max_order_capital}
                helperText={validationErrors.max_order_capital}
              />
            </Box>

            <TextField
              fullWidth
              label="Allowed Slippage (%)"
              value={formData.allowed_slippage}
              onChange={handleFormChange('allowed_slippage')}
              margin="normal"
              type="number"
              step="0.1"
              helperText="e.g., 0.1 for 0.1%"
            />

            {/* Position Mode Settings - Only for Futures vs Futures */}
            {formData.trading_pair && (() => {
              const selectedPair = tradingPairs.find(p => p.id === formData.trading_pair);
              const isFuturesVsFutures = selectedPair &&
                selectedPair.target_market_type !== 'SPOT' &&
                selectedPair.origin_market_type !== 'SPOT';

              if (!isFuturesVsFutures) return null;

              return (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Position Mode
                  </Typography>
                  <Box display="flex" gap={2} alignItems="center">
                    <TextField
                      select
                      label="Position Mode"
                      value={formData.position_mode}
                      onChange={handleFormChange('position_mode')}
                      size="small"
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="bidirectional">Bidirectional</MenuItem>
                      <MenuItem value="unidirectional">Unidirectional</MenuItem>
                    </TextField>
                    {formData.position_mode === 'unidirectional' && (
                      <TextField
                        select
                        label="Allowed Direction"
                        value={formData.allowed_direction}
                        onChange={handleFormChange('allowed_direction')}
                        size="small"
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="ls_to_sl">LS → SL</MenuItem>
                        <MenuItem value="sl_to_ls">SL → LS</MenuItem>
                      </TextField>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {formData.position_mode === 'bidirectional'
                      ? 'Normal mode: Can trade in both directions freely.'
                      : formData.allowed_direction === 'ls_to_sl'
                        ? 'Only open LS positions. SL only closes existing LS positions.'
                        : 'Only open SL positions. LS only closes existing SL positions.'}
                  </Typography>
                </Box>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', px: 3 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={Object.keys(validationErrors).length > 0}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Position Management Dialog */}
      <Dialog
        open={manualOrderDialogOpen}
        onClose={handleCloseManualOrder}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manual Position Management - {selectedTrigger?.trading_pair_symbol}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Current Positions Info */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
              {(() => {
                // Calculate totals for this trading pair
                const tradingPairTriggers = triggers.filter(t => t.trading_pair === selectedTrigger?.trading_pair);

                // Memory totals
                let mTotalTarget = 0, mTotalOrigin = 0;
                tradingPairTriggers.forEach(t => {
                  const memState = memoryStates[t.uuid];
                  if (memState?.positions) {
                    mTotalTarget += parseFloat(memState.positions.target) || 0;
                    mTotalOrigin += parseFloat(memState.positions.origin) || 0;
                  }
                });

                // DB totals
                let dbTotalTarget = 0, dbTotalOrigin = 0;
                tradingPairTriggers.forEach(t => {
                  dbTotalTarget += parseFloat(t.target_position) || 0;
                  dbTotalOrigin += parseFloat(t.origin_position) || 0;
                });

                // Current trigger values
                const memState = selectedTrigger ? memoryStates[selectedTrigger.uuid] : null;
                const mTarget = memState?.positions?.target;
                const mOrigin = memState?.positions?.origin;
                const dbTarget = selectedTrigger?.target_position;
                const dbOrigin = selectedTrigger?.origin_position;
                const exTarget = selectedTrigger?.exchange_target_position;
                const exOrigin = selectedTrigger?.exchange_origin_position;

                // Calculate EX difference (target + origin should be ~0 for hedged positions)
                const exDiff = (exTarget != null && exOrigin != null) ? (exTarget + exOrigin) : null;

                const formatPos = (val) => val != null ? val.toFixed(4) : '-';
                const getColor = (val) => val > 0 ? 'success.main' : val < 0 ? 'error.main' : 'text.secondary';

                return (
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {/* Memory - This trigger */}
                    <Box display="flex" gap={1}>
                      <Typography variant="body2" sx={{ minWidth: 70, color: 'info.main' }}>M:</Typography>
                      <Typography variant="body2" sx={{ color: getColor(mTarget) }}>{formatPos(mTarget)}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>/</Typography>
                      <Typography variant="body2" sx={{ color: getColor(mOrigin) }}>{formatPos(mOrigin)}</Typography>
                    </Box>
                    {/* Memory - Total for trading pair */}
                    <Box display="flex" gap={1}>
                      <Typography variant="body2" sx={{ minWidth: 70, color: 'info.main' }}>M TOTAL:</Typography>
                      <Typography variant="body2" sx={{ color: getColor(mTotalTarget) }}>{formatPos(mTotalTarget)}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>/</Typography>
                      <Typography variant="body2" sx={{ color: getColor(mTotalOrigin) }}>{formatPos(mTotalOrigin)}</Typography>
                    </Box>
                    {/* DB - This trigger */}
                    <Box display="flex" gap={1} mt={0.5}>
                      <Typography variant="body2" sx={{ minWidth: 70 }}>DB:</Typography>
                      <Typography variant="body2" sx={{ color: getColor(dbTarget) }}>{formatPos(dbTarget)}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>/</Typography>
                      <Typography variant="body2" sx={{ color: getColor(dbOrigin) }}>{formatPos(dbOrigin)}</Typography>
                    </Box>
                    {/* DB - Total for trading pair */}
                    <Box display="flex" gap={1}>
                      <Typography variant="body2" sx={{ minWidth: 70 }}>DB TOTAL:</Typography>
                      <Typography variant="body2" sx={{ color: getColor(dbTotalTarget) }}>{formatPos(dbTotalTarget)}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>/</Typography>
                      <Typography variant="body2" sx={{ color: getColor(dbTotalOrigin) }}>{formatPos(dbTotalOrigin)}</Typography>
                    </Box>
                    {/* Exchange positions */}
                    <Box display="flex" gap={1} mt={0.5}>
                      <Typography variant="body2" sx={{ minWidth: 70, fontWeight: 'bold' }}>EX:</Typography>
                      <Typography variant="body2" sx={{ color: getColor(exTarget), fontWeight: 'bold' }}>{formatPos(exTarget)}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>/</Typography>
                      <Typography variant="body2" sx={{ color: getColor(exOrigin), fontWeight: 'bold' }}>{formatPos(exOrigin)}</Typography>
                      {exDiff != null && Math.abs(exDiff) > 0.0001 && (
                        <Typography variant="body2" sx={{ color: 'warning.main', ml: 1 }}>
                          (diff: {exDiff > 0 ? '+' : ''}{exDiff.toFixed(4)})
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })()}
            </Paper>

            {/* Target Exchange Section */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                {selectedTrigger && (
                  <ExchangeIcon
                    exchangeName={tradingPairs.find(tp => tp.id === selectedTrigger.trading_pair)?.target_exchange_name}
                    size={20}
                  />
                )}
                <Typography variant="subtitle2">
                  Target: {selectedTrigger && tradingPairs.find(tp => tp.id === selectedTrigger.trading_pair)?.target_exchange_name}
                  ({selectedTrigger && tradingPairs.find(tp => tp.id === selectedTrigger.trading_pair)?.target_market_type})
                </Typography>
              </Box>
              <TextField
                label="Quantity"
                type="number"
                value={manualOrderData.target_quantity}
                onChange={(e) => setManualOrderData({ ...manualOrderData, target_quantity: e.target.value })}
                fullWidth
                margin="dense"
                inputProps={{ step: '0.0001', min: '0' }}
              />
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <Typography variant="body2">Side:</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={manualOrderData.target_side === 'sell'}
                      onChange={(e) => setManualOrderData({
                        ...manualOrderData,
                        target_side: e.target.checked ? 'sell' : 'buy'
                      })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'error.main',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'error.main',
                        },
                        '& .MuiSwitch-switchBase': {
                          color: 'success.main',
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: 'success.main',
                        }
                      }}
                    />
                  }
                  label={manualOrderData.target_side === 'buy' ? 'LONG (BUY)' : 'SHORT (SELL)'}
                />
              </Box>
            </Box>

            {/* Origin Exchange Section */}
            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                {selectedTrigger && (
                  <ExchangeIcon
                    exchangeName={tradingPairs.find(tp => tp.id === selectedTrigger.trading_pair)?.origin_exchange_name}
                    size={20}
                  />
                )}
                <Typography variant="subtitle2">
                  Origin: {selectedTrigger && tradingPairs.find(tp => tp.id === selectedTrigger.trading_pair)?.origin_exchange_name}
                  ({selectedTrigger && tradingPairs.find(tp => tp.id === selectedTrigger.trading_pair)?.origin_market_type})
                </Typography>
              </Box>
              <TextField
                label="Quantity"
                type="number"
                value={manualOrderData.origin_quantity}
                onChange={(e) => setManualOrderData({ ...manualOrderData, origin_quantity: e.target.value })}
                fullWidth
                margin="dense"
                inputProps={{ step: '0.0001', min: '0' }}
              />
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <Typography variant="body2">Side:</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={manualOrderData.origin_side === 'sell'}
                      onChange={(e) => setManualOrderData({
                        ...manualOrderData,
                        origin_side: e.target.checked ? 'sell' : 'buy'
                      })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'error.main',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'error.main',
                        },
                        '& .MuiSwitch-switchBase': {
                          color: 'success.main',
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: 'success.main',
                        }
                      }}
                    />
                  }
                  label={manualOrderData.origin_side === 'buy' ? 'LONG (BUY)' : 'SHORT (SELL)'}
                />
              </Box>
            </Box>

            {/* Track Position Toggle */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={manualOrderData.track_position}
                    onChange={(e) => setManualOrderData({
                      ...manualOrderData,
                      track_position: e.target.checked
                    })}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Track in Position & PnL
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Record trades in database and recalculate realized PnL
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {manualOrderData.track_position ? (
              <Typography variant="caption" color="info.main" display="block" sx={{ mt: 2 }}>
                ℹ️ Trades will be recorded in TradeHistory and PnL will be recalculated. Enter 0 to skip an exchange.
              </Typography>
            ) : (
              <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 2 }}>
                ⚠️ Emergency tool: Orders placed directly without memory/DB tracking. Enter 0 to skip an exchange.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', px: 3 }}>
          <Button onClick={handleCloseManualOrder}>Cancel</Button>
          <Button
            onClick={handleExecuteManualOrder}
            variant="contained"
            color="primary"
            disabled={manualOrderLoading}
          >
            {manualOrderLoading ? 'Executing...' : 'Execute Orders'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Capital Detail Dialog */}
      <Dialog open={capitalDetailOpen} onClose={handleCloseCapitalDetail} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {selectedCapitalTradingPair && (
              <CryptoIcon
                symbol={tradingPairs.find(tp => tp.id === selectedCapitalTradingPair)?.symbol}
                size={24}
              />
            )}
            <Typography variant="h6">
              Trading Pair Capital Summary
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedCapitalTradingPair && (() => {
            const detail = getCapitalDetailForTradingPair(selectedCapitalTradingPair);
            return (
              <Box>
                {/* Trading Pair Info */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {detail.tradingPair?.symbol}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <ExchangeIcon exchangeName={detail.tradingPair?.target_exchange_name} size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Target: {detail.tradingPair?.target_exchange_name} ({detail.tradingPair?.target_quote_asset})
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ExchangeIcon exchangeName={detail.tradingPair?.origin_exchange_name} size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Origin: {detail.tradingPair?.origin_exchange_name} ({detail.tradingPair?.origin_quote_asset})
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {detail.triggerCount} trigger(s) for this trading pair
                  </Typography>
                </Box>

                {/* Capital Cap */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Total Capital Cap
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatWithCommas(Math.round(detail.totalCapitalCap))} {detail.capUnit}
                  </Typography>
                </Box>

                {/* Memory (M) Capital */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="info.main" gutterBottom>
                    Memory (M) - Entered Capital
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2">
                        Target: <strong>{formatWithCommas(Math.round(detail.totalMemoryTargetEntered))}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Origin: <strong>{formatWithCommas(Math.round(detail.totalMemoryOriginEntered))}</strong>
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="h6" color="info.main">
                        {detail.memoryUsagePercent.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        of cap
                      </Typography>
                    </Box>
                  </Box>
                  {/* Progress bar */}
                  <Box sx={{ mt: 1, height: 8, bgcolor: 'rgba(33, 150, 243, 0.2)', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{
                      width: `${Math.min(100, detail.memoryUsagePercent)}%`,
                      height: '100%',
                      bgcolor: 'info.main',
                      transition: 'width 0.3s'
                    }} />
                  </Box>
                </Box>

                {/* DB Capital */}
                <Box sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.1)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="secondary" gutterBottom>
                    Database (DB) - Entered Capital
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2">
                        Target: <strong>{formatWithCommas(Math.round(detail.totalDbTargetEntered))} {detail.targetUnit}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Origin: <strong>{formatWithCommas(Math.round(detail.totalDbOriginEntered))} {detail.originUnit}</strong>
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="h6" color="secondary">
                        {detail.dbUsagePercent.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        of cap
                      </Typography>
                    </Box>
                  </Box>
                  {/* Progress bar */}
                  <Box sx={{ mt: 1, height: 8, bgcolor: 'rgba(156, 39, 176, 0.2)', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{
                      width: `${Math.min(100, detail.dbUsagePercent)}%`,
                      height: '100%',
                      bgcolor: 'secondary.main',
                      transition: 'width 0.3s'
                    }} />
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCapitalDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for validation/action notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? null : 6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Trade Execution Notifications Container */}
      <Box
        sx={{
          position: 'fixed',
          bottom: isMobile ? 8 : 16,
          right: isMobile ? 8 : 16,
          zIndex: 1300,
          maxWidth: isMobile ? 240 : 400,
        }}
      >
        {notifications.map((notification) => (
          <Slide
            key={notification.id}
            direction="left"
            in={true}
            mountOnEnter
            unmountOnExit
          >
            <div>
              <TradeNotification
                notification={notification}
                onClose={() => handleCloseNotification(notification.id)}
              />
            </div>
          </Slide>
        ))}
      </Box>

      {/* Shared confirmation dialog (delete, emergency-stop, ...) */}
      {confirmDialog}
    </Box>
  );
}

export default Triggers;
